package com.qrkontaq.ui.landing

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document

/**
 * Fonts, keyframes and utility classes for the platform landing, injected once per page.
 * Everything is plain CSS + a vanilla-JS runtime (intro scan, scroll engine with pinned
 * sections, reveal, spotlight, tilt, magnetic buttons, count-up, interactive QR) so the
 * page stays a static Kobweb export with zero dependencies.
 *
 * Class naming: the redesigned landing uses the `qx-` prefix; `ql-` classes are kept for
 * the product mocks (phone / admin / browser), the legal & compare pages and shared
 * widgets (buttons, language menus, cursor) that survived the redesign.
 */
@Composable
internal fun InjectLandingStyles() {
    LaunchedEffect(Unit) {
        // The intro boot lives in <head> so the static export runs it while app.js is
        // still downloading: the DOMContentLoaded-gated runtime below only fires after
        // the bundle executes, which used to keep the prerendered hero hidden (it is
        // CSS-gated on html.qx-ready) for the whole download. It is idempotent, so the
        // hydration re-run of this effect never restarts an intro the baked copy began.
        if (document.getElementById("ql-intro-js") == null) {
            val introScript = document.createElement("script")
            introScript.setAttribute("id", "ql-intro-js")
            introScript.textContent = INTRO_BOOT_JS
            document.head?.appendChild(introScript)
        }

        val fontsHref =
            "https://fonts.googleapis.com/css2?family=Unbounded:wght@400;500;600;700;800" +
                "&family=Playfair+Display:ital,wght@0,600;0,700;1,600&display=swap"
        val link = document.getElementById("ql-landing-fonts") ?: document.createElement("link").also {
            it.setAttribute("id", "ql-landing-fonts")
            it.setAttribute("rel", "stylesheet")
            document.head?.appendChild(it)
        }
        link.setAttribute("href", fontsHref)

        val style = document.getElementById("ql-landing-styles") ?: document.createElement("style").also {
            it.setAttribute("id", "ql-landing-styles")
            document.head?.appendChild(it)
        }
        style.textContent = LANDING_CSS

        // Boot must run twice per load: the baked copy fires at DOMContentLoaded on
        // the static snapshot, but hydration then replaces the nodes it captured
        // (cursor dot/ring, header, scanline...), leaving its handlers writing to
        // detached elements — with the native cursor still hidden by ql-cursor-on.
        // The baked <script> node sits outside _kobweb-root and survives the wipe,
        // so an existence check would skip the re-run: drop it and inject a fresh
        // copy to rebind everything to the live DOM. (A <script> only executes when
        // first inserted, so build it fully before appending.)
        document.getElementById("ql-landing-js")?.remove()
        val script = document.createElement("script")
        script.setAttribute("id", "ql-landing-js")
        script.textContent = LANDING_JS
        document.body?.appendChild(script)
    }
}

// language=JavaScript
private const val INTRO_BOOT_JS = """
(function(){
  if(window.qxIntroBooted)return;
  window.qxIntroBooted=true;
  var root=document.documentElement;
  function endIntro(){root.classList.add('qx-ready');root.classList.add('qx-intro-done');}
  var reduced=false,seen=false;
  try{reduced=window.matchMedia('(prefers-reduced-motion: reduce)').matches;}catch(e){}
  try{seen=!!sessionStorage.getItem('qxi');sessionStorage.setItem('qxi','1');}catch(e){}
  if(reduced||seen){endIntro();return;}
  setTimeout(endIntro,1800);
  document.addEventListener('click',function(e){
    var t=e.target;
    if(t&&t.closest&&t.closest('.qx-intro'))endIntro();
  },true);
})();
"""

// language=CSS
private const val LANDING_CSS = """
html{scroll-behavior:smooth;scrollbar-width:none;-ms-overflow-style:none}
html::-webkit-scrollbar,body::-webkit-scrollbar{width:0;height:0;display:none}

/* ============================ dark landing shell ============================ */
.qx-root{position:relative;background:#050E0A;color:#F2F7F4;font-family:system-ui,-apple-system,'Segoe UI',sans-serif;overflow-x:clip;min-height:100vh}
.qx-root *{box-sizing:border-box}
.qx-root a{text-decoration:none;color:inherit}
.qx-root ::selection{background:rgba(52,211,153,.35);color:#fff}
.qx-display{font-family:'Unbounded',system-ui,sans-serif;letter-spacing:-0.02em}
.qx-serif{font-family:'Playfair Display',Georgia,serif;font-style:italic}
.qx-wrap{width:100%;max-width:1180px;margin:0 auto;padding:0 26px}
.qx-section{position:relative;z-index:1;scroll-margin-top:90px}

/* ---------- global scene: one continuous canvas behind everything ---------- */
.qx-scene{position:fixed;inset:0;z-index:0;pointer-events:none;overflow:hidden}
/* No blur filter here: soft multi-stop gradients read the same and re-render
   an order of magnitude cheaper while the blobs move every frame. */
.qx-scene-blobs{position:absolute;inset:0}
.qx-scene-blobs i{position:absolute;left:-390px;top:-390px;width:780px;height:780px;border-radius:50%;will-change:transform}
.qx-scene-blobs i:nth-child(1){background:radial-gradient(circle,rgba(15,157,110,.32) 0%,rgba(15,157,110,.14) 38%,transparent 68%);transform:translate3d(-8vw,-8vh,0) scale(1.25)}
.qx-scene-blobs i:nth-child(2){background:radial-gradient(circle,rgba(126,226,188,.17) 0%,rgba(126,226,188,.07) 40%,transparent 68%);transform:translate3d(82vw,6vh,0)}
.qx-scene-blobs i:nth-child(3){background:radial-gradient(circle,rgba(255,107,91,.13) 0%,rgba(255,107,91,.05) 40%,transparent 68%);transform:translate3d(40vw,95vh,0) scale(.8)}
.qx-scene-grid{position:absolute;inset:-60px 0;background-image:linear-gradient(rgba(255,255,255,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.04) 1px,transparent 1px);background-size:56px 56px;will-change:transform;mask-image:radial-gradient(80% 64% at 50% 42%,#000 28%,transparent 100%);-webkit-mask-image:radial-gradient(80% 64% at 50% 42%,#000 28%,transparent 100%)}
.qx-scene .qx-bit{position:absolute}

/* ---------- the scan thread drawn through the whole page ---------- */
.qx-thread-wrap{position:absolute;left:0;top:0;width:100%;z-index:0;pointer-events:none}
.qx-thread-wrap svg{display:block;width:100%}
@media(max-width:960px){.qx-thread-wrap{display:none}}

/* ---------- scroll-choreographed blocks (continuous, both directions) ---------- */
[data-flow]{opacity:0;will-change:transform,opacity}

/* ---------- giant word band between scenes ---------- */
.qx-bandword{position:relative;z-index:1;margin:150px 0 -30px -40vw;white-space:nowrap;font-size:clamp(64px,10vw,150px);font-weight:800;line-height:.9;color:transparent;-webkit-text-stroke:1.5px rgba(126,226,188,.12);pointer-events:none;user-select:none;will-change:transform}
@media(max-width:960px){.qx-bandword{margin-top:90px}}
.qx-noise{position:fixed;inset:0;z-index:96;pointer-events:none;opacity:.5;background-image:url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)' opacity='0.05'/%3E%3C/svg%3E")}

/* ---------- reveal on scroll ---------- */
.qx-reveal{opacity:0;transform:translateY(30px);transition:opacity .8s cubic-bezier(.22,.61,.36,1),transform .8s cubic-bezier(.22,.61,.36,1);transition-delay:var(--d,0s)}
.qx-reveal.in{opacity:1;transform:none}
.ql-reveal{opacity:0;transform:translateY(26px);transition:opacity .7s cubic-bezier(.22,.61,.36,1),transform .7s cubic-bezier(.22,.61,.36,1);transition-delay:var(--d,0s)}
.ql-reveal.in{opacity:1;transform:none}

/* ---------- hero entrance (gated by html.qx-ready, set when the intro ends) ---------- */
.qx-mask{display:inline-block;overflow:hidden;vertical-align:bottom;padding-bottom:.09em;margin-bottom:-.09em}
.qx-mask .w{display:inline-block;transform:translateY(130%) rotate(7deg);transform-origin:0 100%}
html.qx-ready .qx-mask .w{animation:qx-word 1s cubic-bezier(.19,.8,.22,1) forwards;animation-delay:var(--d,0s)}
.qx-fade{opacity:0;transform:translateY(26px)}
html.qx-ready .qx-fade{animation:qx-fadeup .85s cubic-bezier(.22,.61,.36,1) forwards;animation-delay:var(--d,0s)}

/* ---------- intro: QR assembles, laser scans, page opens ---------- */
.qx-intro{position:fixed;inset:0;z-index:500;background:#040A07;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:26px;cursor:pointer;transition:opacity .55s ease,visibility .55s}
.qx-intro.gone,html.qx-intro-done .qx-intro{opacity:0;visibility:hidden;pointer-events:none}
.qx-intro-box{position:relative;padding:16px;border-radius:20px;border:1px solid rgba(52,211,153,.25)}
.qx-intro-qr{display:grid;grid-template-columns:repeat(11,1fr);gap:3px;width:132px;height:132px}
.qx-intro-qr i{background:#34D399;border-radius:1.5px;opacity:0;transform:scale(.2);animation:qx-cell-pop .45s cubic-bezier(.22,.61,.36,1) forwards;animation-delay:var(--d,0s)}
.qx-intro-laser{position:absolute;left:6px;right:6px;top:0;height:2px;border-radius:2px;background:linear-gradient(90deg,transparent,#7EE2BC,transparent);box-shadow:0 0 18px 3px rgba(52,211,153,.6);animation:qx-laser 1.15s ease-in-out .35s both}
.qx-intro-brand{font-size:15px;font-weight:600;color:rgba(242,247,244,.85);letter-spacing:.3em;text-transform:uppercase;opacity:0;animation:qx-fadeup .6s ease .7s forwards}

/* ============================ header ============================ */
.qx-header{position:fixed;top:14px;left:0;right:0;z-index:120}
.qx-header-in{display:flex;align-items:center;justify-content:space-between;height:62px;padding:0 20px;border-radius:20px;border:1px solid transparent;color:#fff;transition:background .35s ease,border-color .35s ease,box-shadow .35s ease,backdrop-filter .35s ease}
.qx-header.scrolled .qx-header-in{background:rgba(6,17,13,.7);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border-color:rgba(255,255,255,.09);box-shadow:0 14px 44px rgba(0,0,0,.4)}
.ql-logo{display:flex;align-items:center;gap:10px;font-size:19px;font-weight:600;cursor:pointer}
.ql-logo-mark{width:25px;height:25px;flex:none;display:inline-flex}
.ql-logo-mark .ql-svg{width:100%;height:100%}
.ql-logo-mark .dot{animation:ql-blink 2.4s ease-in-out infinite}
.ql-nav{display:flex;align-items:center;gap:24px;font-size:14.5px}
.ql-nav-link{position:relative;opacity:.8;transition:opacity .2s;cursor:pointer;padding:4px 0}
.ql-nav-link::after{content:'';position:absolute;left:0;right:100%;bottom:0;height:1.5px;background:#34D399;transition:right .3s cubic-bezier(.22,.61,.36,1)}
.ql-nav-link:hover{opacity:1}
.ql-nav-link:hover::after{right:0}
.ql-header-cta .short{display:none}
@media(max-width:820px){
  .ql-nav-links{display:none}
  .ql-nav{gap:10px}
  .qx-header .ql-lang{display:none}
  .qx-header .ql-logo-word{display:none}
  .ql-header-cta .full{display:none}
  .ql-header-cta .short{display:inline}
  .qx-header{top:8px}
  .qx-header-in{height:56px;padding:0 14px;position:relative}
  /* Logo left, burger right, the CTA dead-centre of the bar itself. Doubled
     selector so it outranks the later .ql-btn base rule (same specificity
     otherwise); overriding its magnetic transform is fine — no magnet on touch. */
  .qx-header .ql-header-cta{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%)}
}

/* ---------- mobile burger menu ---------- */
.ql-mmenu-wrap{display:none}
.ql-burgerbtn{width:38px;height:38px;flex:none;border-radius:11px;border:1px solid rgba(255,255,255,.22);background:rgba(255,255,255,.05);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4.5px;cursor:pointer;transition:background .25s}
.ql-burgerbtn i{width:16px;height:1.8px;border-radius:2px;background:#fff;transition:transform .25s ease,opacity .2s}
.ql-burgerbtn.open i:nth-child(1){transform:translateY(6.3px) rotate(45deg)}
.ql-burgerbtn.open i:nth-child(2){opacity:0}
.ql-burgerbtn.open i:nth-child(3){transform:translateY(-6.3px) rotate(-45deg)}
.ql-mmenu-backdrop{position:fixed;inset:0;z-index:124;background:rgba(4,10,8,.5);backdrop-filter:blur(3px);-webkit-backdrop-filter:blur(3px)}
.ql-mmenu{position:fixed;top:74px;left:14px;right:14px;z-index:126;background:rgba(10,22,18,.97);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);border:1px solid rgba(255,255,255,.12);border-radius:18px;padding:10px;box-shadow:0 24px 60px rgba(0,0,0,.5);animation:ql-lang-in .2s cubic-bezier(.22,.61,.36,1)}
.ql-mmenu-link{display:block;padding:13px 14px;border-radius:12px;font-size:15.5px;font-weight:600;color:rgba(255,255,255,.88)}
.ql-mmenu-link:active{background:rgba(255,255,255,.08)}
.ql-mmenu-langs{display:flex;flex-wrap:wrap;gap:6px;border-top:1px solid rgba(255,255,255,.1);margin-top:8px;padding:12px 4px 4px}
.ql-mmenu-lang{flex:1;min-width:42px;text-align:center;font-size:12px;font-weight:700;color:rgba(255,255,255,.7);border:1px solid rgba(255,255,255,.16);border-radius:9px;padding:9px 0;cursor:pointer;transition:background .2s,border-color .2s,color .2s}
.ql-mmenu-lang.on{background:rgba(15,157,110,.2);border-color:rgba(126,226,188,.45);color:#7EE2BC}
.ql-mmenu-cta{display:flex;justify-content:center;margin-top:10px}
@media(max-width:820px){.ql-mmenu-wrap{display:block}}

/* ---------- language dropdown ---------- */
.ql-lang{position:relative}
.ql-lang-btn{display:inline-flex;align-items:center;gap:7px;color:#fff;font-size:13px;font-weight:600;padding:8px 12px;border-radius:11px;border:1px solid rgba(255,255,255,.22);background:rgba(255,255,255,.05);cursor:pointer;transition:background .25s,border-color .25s;user-select:none}
.ql-lang-btn:hover{background:rgba(255,255,255,.1);border-color:rgba(255,255,255,.4)}
.ql-lang-globe{width:15px;height:15px;display:inline-flex;flex:none}
.ql-lang-globe .ql-svg{width:100%;height:100%}
.ql-lang-caret{font-size:10px;opacity:.7;transition:transform .25s}
.ql-lang.open .ql-lang-caret{transform:rotate(180deg)}
.ql-lang-backdrop{position:fixed;inset:0;z-index:128}
.ql-lang-menu{position:absolute;top:calc(100% + 10px);right:0;min-width:172px;background:rgba(10,22,18,.96);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);border:1px solid rgba(255,255,255,.12);border-radius:14px;padding:6px;box-shadow:0 20px 50px rgba(0,0,0,.45);z-index:130;animation:ql-lang-in .18s cubic-bezier(.22,.61,.36,1)}
.ql-lang-item{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:9px 12px;border-radius:9px;font-size:13.5px;color:rgba(255,255,255,.85);cursor:pointer;transition:background .15s,color .15s;white-space:nowrap}
.ql-lang-item:hover{background:rgba(255,255,255,.08);color:#fff}
.ql-lang-item.on{color:#7EE2BC;background:rgba(15,157,110,.14)}
.ql-lang-item .check{font-size:12px;font-weight:700}
@keyframes ql-lang-in{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}

/* ---------- buttons ---------- */
.ql-btn{position:relative;display:inline-flex;align-items:center;justify-content:center;gap:8px;background:linear-gradient(135deg,#12B981,#0F9D6E);color:#fff;font-weight:600;font-size:16px;border:none;border-radius:14px;padding:15px 30px;cursor:pointer;overflow:hidden;box-shadow:0 10px 30px rgba(15,157,110,.35);transform:translate3d(var(--tx,0px),var(--ty,0px),0) scale(var(--sc,1));transition:transform .22s cubic-bezier(.22,.61,.36,1),box-shadow .25s}
.ql-btn:hover{box-shadow:0 16px 40px rgba(15,157,110,.45)}
.ql-btn:active{--sc:.97}
.ql-btn::after{content:'';position:absolute;top:0;left:-80%;width:36%;height:100%;background:linear-gradient(105deg,transparent,rgba(255,255,255,.5),transparent);transform:skewX(-20deg);animation:ql-shine 3.4s ease-in-out infinite}
.ql-btn.small{padding:9px 18px;font-size:14px;border-radius:11px}
.ql-btn.big{padding:18px 38px;font-size:17px;border-radius:16px}
.ql-btn.white{background:#fff;color:#0B7A55;box-shadow:0 10px 30px rgba(0,0,0,.22)}
.ql-btn-ghost{display:inline-flex;align-items:center;gap:8px;color:#fff;font-weight:500;font-size:15.5px;padding:15px 22px;border-radius:14px;border:1px solid rgba(255,255,255,.22);background:rgba(255,255,255,.05);cursor:pointer;transform:translate3d(var(--tx,0px),var(--ty,0px),0);transition:background .25s,border-color .25s,transform .22s cubic-bezier(.22,.61,.36,1)}
.ql-btn-ghost:hover{background:rgba(255,255,255,.1);border-color:rgba(255,255,255,.4)}
.ql-btn-outline{display:inline-flex;align-items:center;justify-content:center;color:#7EE2BC;font-weight:600;font-size:14px;padding:11px 20px;border-radius:12px;border:1.5px solid rgba(126,226,188,.5);background:transparent;cursor:pointer;transform:translate3d(var(--tx,0px),var(--ty,0px),0);transition:background .2s,color .2s,border-color .2s,transform .22s cubic-bezier(.22,.61,.36,1)}
.ql-btn-outline:hover{background:#0F9D6E;border-color:#0F9D6E;color:#fff}

/* ============================ hero (transparent: the scene shows through) ============================ */
.qx-hero{position:relative;z-index:1;min-height:100svh;display:flex;align-items:center;overflow:hidden;padding:150px 0 110px}
.ql-aurora{position:absolute;inset:-15%;filter:blur(70px);pointer-events:none}
.ql-aurora i{position:absolute;border-radius:50%}
.ql-aurora i:nth-child(1){width:640px;height:640px;left:-150px;top:-170px;background:radial-gradient(circle,rgba(15,157,110,.38),transparent 65%);animation:ql-drift 18s ease-in-out infinite alternate}
.ql-aurora i:nth-child(2){width:520px;height:520px;right:-120px;top:60px;background:radial-gradient(circle,rgba(126,226,188,.2),transparent 65%);animation:ql-drift 22s ease-in-out -6s infinite alternate-reverse}
.ql-aurora i:nth-child(3){width:480px;height:480px;left:34%;bottom:-230px;background:radial-gradient(circle,rgba(255,107,91,.15),transparent 65%);animation:ql-drift 26s ease-in-out -12s infinite alternate}
.ql-hero-grid{position:absolute;inset:0;background-image:linear-gradient(rgba(255,255,255,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.04) 1px,transparent 1px);background-size:56px 56px;mask-image:radial-gradient(900px 620px at 55% 22%,#000 18%,transparent 76%);-webkit-mask-image:radial-gradient(900px 620px at 55% 22%,#000 18%,transparent 76%);pointer-events:none}
.qx-heroword{position:absolute;left:-2%;bottom:-2.5vw;font-size:clamp(90px,17vw,260px);font-weight:800;line-height:.9;white-space:nowrap;color:transparent;-webkit-text-stroke:1.5px rgba(126,226,188,.09);pointer-events:none;user-select:none}
.qx-bit{position:absolute;border-radius:3px;pointer-events:none;animation:qx-bitfloat var(--t,9s) ease-in-out var(--dd,0s) infinite alternate}
.qx-bit.f{background:rgba(52,211,153,.35)}
.qx-bit.o{border:1.5px solid rgba(126,226,188,.3)}
.qx-hero-in{position:relative;z-index:2;display:flex;align-items:center;gap:48px;width:100%}
.qx-hero-copy{flex:1 1 560px;min-width:0}
.qx-badge{display:inline-flex;align-items:center;gap:8px;font-size:13px;font-weight:500;color:#7EE2BC;background:rgba(15,157,110,.14);border:1px solid rgba(126,226,188,.3);border-radius:999px;padding:7px 14px}
.qx-badge i{width:7px;height:7px;border-radius:50%;background:#34D399;animation:ql-pulse-dot 1.8s ease-in-out infinite}
.qx-h1{font-size:clamp(38px,5.6vw,74px);font-weight:700;line-height:1.05;margin:24px 0 0;color:#fff}
.ql-grad-text{background:linear-gradient(90deg,#34D399,#7EE2BC,#A7F3D0,#34D399);background-size:300% 100%;-webkit-background-clip:text;background-clip:text;color:transparent;animation:ql-hue 7s linear infinite}
.qx-hero-sub{font-size:18px;line-height:1.65;color:rgba(242,247,244,.7);max-width:540px;margin:24px 0 0}
.qx-hero-ctas{display:flex;align-items:center;flex-wrap:wrap;gap:14px;margin-top:34px}
.qx-hero-note{display:flex;align-items:center;gap:18px;flex-wrap:wrap;margin-top:24px;font-size:13.5px;color:rgba(242,247,244,.52)}
.qx-hero-note span{display:inline-flex;align-items:center;gap:7px}
.qx-hero-note b{color:#7EE2BC;font-weight:600}
.qx-shint{position:absolute;left:50%;bottom:26px;transform:translateX(-50%);z-index:3;width:26px;height:42px;border:1.5px solid rgba(242,247,244,.28);border-radius:14px;pointer-events:none}
.qx-shint::after{content:'';position:absolute;left:50%;top:7px;width:3px;height:9px;margin-left:-1.5px;border-radius:3px;background:#34D399;animation:qx-hint 1.9s ease-in-out infinite}
@media(max-height:760px){.qx-shint{display:none}}

/* ---------- hero visual: phone + orbit (product mock, mirrors the real guest menu) ---------- */
.ql-hero-visual{flex:1 1 420px;min-width:0;display:flex;justify-content:center;position:relative;min-height:620px}
.ql-ring{position:absolute;top:50%;left:50%;border:1px dashed rgba(255,255,255,.14);border-radius:50%;pointer-events:none}
.ql-ring.r1{width:540px;height:540px;margin:-270px 0 0 -270px;animation:ql-spin 60s linear infinite}
.ql-ring.r2{width:400px;height:400px;margin:-200px 0 0 -200px;animation:ql-spin 45s linear infinite reverse}
.ql-ring .dot{position:absolute;top:-4px;left:50%;width:8px;height:8px;border-radius:50%;background:#34D399;box-shadow:0 0 12px #34D399}
.ql-phone-wrap{position:relative;transform:perspective(1400px) rotateY(var(--ry,-11deg)) rotateX(var(--rx,4deg));transition:transform .4s ease-out;transform-style:preserve-3d}
.ql-phone{width:272px;border-radius:40px;background:#101613;padding:10px;box-shadow:0 50px 100px rgba(0,0,0,.55),0 0 0 1px rgba(255,255,255,.09),inset 0 0 0 1px rgba(255,255,255,.05);animation:ql-float 6.5s ease-in-out infinite}
.ql-screen{position:relative;border-radius:31px;overflow:hidden;background:#FAF8F5;height:552px}
.ql-screen::after{content:'';position:absolute;inset:0;background:linear-gradient(115deg,transparent 42%,rgba(255,255,255,.35) 50%,transparent 58%);transform:translateX(-120%);animation:ql-screen-shine 6s ease-in-out 2s infinite}
.ql-notch{position:absolute;top:8px;left:50%;transform:translateX(-50%);width:78px;height:20px;border-radius:12px;background:#101613;z-index:5}
.ql-mcover{position:relative;height:150px;background:url('/landing/cover.jpg') center/cover,linear-gradient(135deg,#C4573A 0%,#5C2117 100%);display:flex;flex-direction:column;justify-content:flex-end;padding:10px;overflow:hidden}
.ql-mcover-scrim{position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,.30) 0%,rgba(0,0,0,.06) 34%,rgba(0,0,0,.74) 100%)}
.ql-mlogo{position:absolute;top:9px;left:9px;width:30px;height:30px;border-radius:9px;background:#FAF8F5;color:#A8442B;font-family:'Unbounded',sans-serif;font-weight:700;font-size:13px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,.35)}
.ql-mtop{position:absolute;top:9px;right:9px;display:flex;align-items:center;gap:5px;z-index:2}
.ql-mlang{display:flex;align-items:center;background:rgba(255,255,255,.92);backdrop-filter:blur(8px);border-radius:999px;padding:2px;box-shadow:0 2px 7px rgba(31,36,33,.18)}
.ql-mlang span{font-size:8px;font-weight:600;color:#1F2421;border-radius:999px;padding:4px 7px;line-height:1}
.ql-mlang span.on{background:#1F2421;color:#fff}
.ql-mburger{flex:none;width:24px;height:24px;border-radius:50%;background:rgba(255,255,255,.92);backdrop-filter:blur(8px);box-shadow:0 1px 6px rgba(31,36,33,.18);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2.5px}
.ql-mburger i{width:9px;height:1.4px;border-radius:1px;background:#1F2421}
.ql-mcover-bottom{position:relative;min-width:0}
.ql-mcover-name{font-family:'Unbounded',sans-serif;font-weight:600;font-size:14.5px;color:#fff;text-shadow:0 2px 14px rgba(0,0,0,.45);white-space:nowrap}
.ql-mcover-meta{display:flex;align-items:center;gap:4px;margin-top:6px;font-size:8.5px;font-weight:600;color:#fff}
.ql-mcover-meta .pill{display:inline-flex;align-items:center;gap:3.5px;background:rgba(255,255,255,.16);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,.30);border-radius:999px;padding:2.5px 7px;white-space:nowrap}
.ql-mcover-meta .dot{width:4.5px;height:4.5px;border-radius:50%;background:#4ADE80}
.ql-mcover-meta .star{color:#FFC862;font-size:8px}
.ql-mrail{display:flex;align-items:center;padding:7px 8px;background:rgba(250,248,245,.95);border-bottom:1px solid rgba(236,232,226,.8)}
.ql-mrail-chips{display:flex;gap:4px;flex:1;min-width:0;overflow:hidden}
.ql-mchip{flex:none;display:inline-flex;align-items:center;font-size:9.5px;font-weight:600;border-radius:999px;padding:5px 10px;background:#fff;color:#1F2421;border:1px solid #ECE8E2}
.ql-mchip.has-img{padding-left:3px}
.ql-mchip img{width:17px;height:17px;border-radius:999px;object-fit:cover;margin-right:5px;border:1px solid #ECE8E2}
.ql-mchip.on{background:#C4573A;color:#fff;border-color:#C4573A}
.ql-mchip.on img{border-color:rgba(255,255,255,.6)}
.ql-mfilter{position:absolute;right:10px;bottom:10px;z-index:3;width:26px;height:26px;border-radius:50%;background:rgba(255,255,255,.18);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,.34);display:inline-flex;align-items:center;justify-content:center;color:#fff}
.ql-mfilter .ql-svg{width:12px;height:12px}
.ql-msec{display:flex;align-items:flex-end;gap:6px;margin:11px 12px 2px}
.ql-msec .num{font-family:'Unbounded',sans-serif;font-size:17px;font-weight:700;line-height:.85;color:transparent;-webkit-text-stroke:1px rgba(196,87,58,.45)}
.ql-msec .t{font-family:'Playfair Display',Georgia,serif;font-size:13px;font-weight:700;color:#1F2421;line-height:1}
.ql-mdish{display:flex;align-items:center;gap:8px;margin:7px 10px 0;background:#fff;border-radius:13px;padding:7px;box-shadow:0 1px 4px rgba(31,36,33,.06);opacity:0;animation:ql-rise-in .6s cubic-bezier(.22,.61,.36,1) forwards}
.ql-mdish-body{flex:1;min-width:0}
.ql-mdish-name{font-size:11px;font-weight:600;color:#1F2421;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ql-mdish-sub{font-size:9px;color:#6B7280;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ql-mdish-nutri{font-size:8.5px;font-weight:600;color:#9AA1A0;margin-top:3px;letter-spacing:.01em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ql-mdish-meta{display:flex;align-items:center;gap:7px;margin-top:5px}
.ql-mdish-price{font-size:11.5px;font-weight:800;color:#C4573A;white-space:nowrap}
.ql-mlikes{display:inline-flex;align-items:center;gap:3px;font-size:9px;font-weight:600;color:#6B7280}
.ql-mheart{display:inline-block;color:#FF6B5B;font-size:9px;line-height:1;animation:ql-heart 2.6s ease-in-out infinite}
.ql-mimg{width:46px;height:46px;flex:none;border-radius:10px;background-size:cover;background-position:center}
.ql-mimg.pizza{background-image:url('/landing/pizza.jpg')}
.ql-mimg.pasta{background-image:url('/landing/pasta.jpg')}
.ql-mimg.dessert{background-image:url('/landing/dessert.jpg')}
.ql-mimg.drink{background-image:url('/landing/drink.jpg')}
.ql-mfoot{display:flex;align-items:center;justify-content:center;gap:5px;margin:10px 0 8px;font-size:8px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#C4BCB1}
.ql-mfoot-mark{width:10px;height:10px;display:inline-flex;color:#C4BCB1}
.ql-mfoot-mark .ql-svg{width:100%;height:100%}
.ql-mfeat{position:relative;margin:8px 10px 7px;height:120px;border-radius:13px;overflow:hidden;background-size:cover;background-position:center;box-shadow:0 4px 13px rgba(31,36,33,.12);opacity:0;animation:ql-rise-in .6s cubic-bezier(.22,.61,.36,1) .3s forwards}
.ql-mfeat-grad{position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,0) 28%,rgba(0,0,0,.74) 100%)}
.ql-mfeat-likes{position:absolute;top:6.5px;right:6.5px;display:inline-flex;align-items:center;gap:3px;font-size:8px;font-weight:600;color:#fff;background:rgba(255,255,255,.16);backdrop-filter:blur(6px);border:1px solid rgba(255,255,255,.30);border-radius:999px;padding:2px 6px}
.ql-fheart{color:#FF8A7A;font-size:7.5px;line-height:1}
.ql-mfeat-bot{position:absolute;left:0;right:0;bottom:0;padding:9px;display:flex;align-items:flex-end;justify-content:space-between;gap:6px}
.ql-mfeat-name{font-family:'Playfair Display',Georgia,serif;font-size:13px;font-weight:700;color:#fff;line-height:1.2;text-shadow:0 1px 12px rgba(0,0,0,.4)}
.ql-mfeat-meta{font-size:8px;color:rgba(255,255,255,.85);margin-top:2px}
.ql-mfeat-nutri{font-size:7.5px;font-weight:600;color:rgba(255,255,255,.72);margin-top:2px;letter-spacing:.02em}
.ql-mfeat-price{flex:none;font-size:10px;font-weight:700;color:#C4573A;background:rgba(255,255,255,0.92);border-radius:999px;padding:4px 8.5px}
.ql-mwaiter{position:absolute;right:10px;bottom:10px;z-index:6;display:inline-flex;align-items:center;gap:4.5px;background:#C4573A;color:#fff;border-radius:999px;padding:8px 11px;font-size:9px;font-weight:600;box-shadow:0 4px 13px rgba(0,0,0,.28)}
.ql-mwaiter .ql-svg{width:10.5px;height:10.5px}
.ql-mcheck{position:absolute;right:10px;bottom:41px;z-index:6;display:inline-flex;align-items:center;gap:4.5px;background:#fff;color:#C4573A;border:1px solid #C4573A;border-radius:999px;padding:7px 10px;font-size:9px;font-weight:600;box-shadow:0 4px 12px rgba(0,0,0,.16)}
.ql-mcheck .ql-svg{width:10.5px;height:10.5px}
.ql-float-card{position:absolute;z-index:6;display:flex;align-items:center;gap:10px;background:rgba(8,20,16,.78);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);border:1px solid rgba(255,255,255,.28);border-radius:16px;padding:12px 15px;color:#fff;font-size:13px;box-shadow:0 18px 48px rgba(0,0,0,.45);animation:ql-float 7s ease-in-out infinite}
.ql-float-card b{font-size:13.5px}
.ql-float-card small{display:block;opacity:.85;color:rgba(255,255,255,.92);font-size:11px;margin-top:3px}
.ql-fc-qr{top:158px;left:-44px;animation-delay:-2s;flex-direction:column;align-items:center;gap:8px;padding:14px}
.ql-fc-rating{top:120px;right:-34px;animation-delay:-4s}
.ql-fc-stop{bottom:96px;left:-46px;animation-delay:-1s}
.ql-fc-scans{bottom:-6px;right:-30px;animation-delay:-5s}
.ql-star{color:#F5C36B}
.ql-toggle{width:30px;height:18px;border-radius:999px;background:#FF6B5B;position:relative;flex:none}
.ql-toggle::after{content:'';position:absolute;top:2px;left:2px;width:14px;height:14px;border-radius:50%;background:#fff;animation:ql-toggle 4s ease-in-out infinite}
.ql-qr{display:grid;grid-template-columns:repeat(11,1fr);gap:2.5px;width:96px;height:96px}
.ql-qr i{background:#fff;border-radius:1.5px;opacity:0;animation:ql-cell .5s ease forwards;animation-delay:var(--d)}
.ql-qr i{transition:transform .16s ease,box-shadow .16s ease,background-color .16s ease}
.ql-fc-ic{width:20px;height:20px;flex:none}
.ql-fc-ic .ql-svg{width:100%;height:100%}
.ql-fc-scans .ql-fc-ic{color:#7EE2BC}
@media(max-width:1060px){.ql-fc-qr{left:-6px}.ql-fc-stop{left:-14px}.ql-fc-rating{right:-8px}.ql-fc-scans{right:-2px}}
@media(min-width:961px) and (max-height:820px){.qx-hero{padding:120px 0 80px}.qx-h1{font-size:clamp(34px,4.8vw,58px)}}
@media(max-width:960px){.qx-hero{padding:120px 0 90px;min-height:0}.qx-hero-in{flex-direction:column-reverse;gap:34px}.ql-hero-visual{min-height:640px;width:100%}.qx-heroword{display:none}}
@media(max-width:420px){.ql-fc-stop,.ql-fc-rating{display:none}.ql-ring.r1{width:420px;height:420px;margin:-210px 0 0 -210px}}

/* ============================ marquee ============================ */
.ql-marquee{position:relative;z-index:8;background:linear-gradient(90deg,#0F9D6E,#0B7A55);color:#fff;transform:rotate(-1.1deg) scale(1.02);margin:-28px 0;padding:16px 0;overflow:hidden;box-shadow:0 20px 60px rgba(11,122,85,.4)}
.ql-mq-track{display:flex;align-items:center;gap:44px;width:max-content;white-space:nowrap;animation:ql-marquee 30s linear infinite}
.ql-mq-item{display:inline-flex;align-items:center;gap:44px;font-family:'Unbounded',sans-serif;font-size:14px;font-weight:500;letter-spacing:.02em}
.ql-mq-item i{width:6px;height:6px;border-radius:50%;background:rgba(255,255,255,.55);flex:none}

/* ============================ chapter heads ============================ */
.qx-chap{position:relative;display:flex;align-items:flex-start;gap:30px;margin-bottom:64px}
/* one-shot laser sweep when the chapter scrolls in */
.qx-chap::after{content:'';position:absolute;left:-3%;right:-3%;top:-12px;height:2px;border-radius:2px;background:linear-gradient(90deg,transparent,#7EE2BC 30%,#7EE2BC 70%,transparent);box-shadow:0 0 18px 3px rgba(52,211,153,.45);opacity:0;pointer-events:none}
.qx-chap.in::after{animation:qx-chapscan 1.1s ease-in-out .05s forwards}
.qx-chap-num{flex:none;font-size:clamp(58px,8vw,110px);font-weight:800;line-height:.82;color:transparent;-webkit-text-stroke:1.6px rgba(126,226,188,.28);user-select:none}
.qx-chap-bd{flex:1;min-width:0}
.qx-chap-top{display:flex;align-items:baseline;gap:16px}
.qx-overline{font-family:'Unbounded',sans-serif;font-size:11.5px;font-weight:600;letter-spacing:.2em;text-transform:uppercase;color:#34D399}
.qx-chap-line{flex:1;border-bottom:2px dotted rgba(242,247,244,.18);transform:translateY(-4px);min-width:30px}
.qx-chap-note{font-family:'Playfair Display',Georgia,serif;font-style:italic;font-size:16px;color:#7EE2BC;white-space:nowrap}
.qx-chap-title{font-size:clamp(30px,4.4vw,54px);font-weight:700;line-height:1.1;color:#fff;margin:16px 0 0}
.qx-chap-sub{font-size:16.5px;line-height:1.65;color:rgba(242,247,244,.6);margin:16px 0 0;max-width:600px}
@media(max-width:700px){.qx-chap{gap:18px;margin-bottom:44px}.qx-chap-note{display:none}}

/* ---------- card spotlight ---------- */
.qx-spot{position:relative}
.qx-spot::before{content:'';position:absolute;inset:0;border-radius:inherit;background:radial-gradient(320px circle at var(--mx,50%) var(--my,50%),rgba(126,226,188,.08),transparent 65%);opacity:0;transition:opacity .35s;pointer-events:none;z-index:1}
.qx-spot:hover::before{opacity:1}

/* ============================ deck (how it works) ============================ */
.qx-how{padding:150px 0 0}
.qx-deck{position:relative;height:320vh}
.qx-deck-pin{position:sticky;top:0;height:100vh;display:grid;place-items:center;overflow:hidden}
.qx-card{grid-area:1/1;position:relative;width:min(960px,92vw);border-radius:30px;padding:54px 58px;display:flex;align-items:center;gap:52px;background:linear-gradient(155deg,#0C2019 0%,#071510 70%);border:1px solid rgba(255,255,255,.1);box-shadow:0 50px 130px rgba(0,0,0,.55);will-change:transform}
.qx-card-l{flex:1.1;min-width:0;position:relative;z-index:2}
.qx-card-r{flex:1;min-width:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px}
.qx-card-num{position:absolute;top:-40px;right:-10px;font-size:clamp(80px,10vw,150px);font-weight:800;line-height:1;color:transparent;-webkit-text-stroke:1.6px rgba(126,226,188,.16);pointer-events:none;user-select:none;z-index:0}
.qx-card-badge{width:52px;height:52px;border-radius:16px;background:rgba(15,157,110,.15);border:1px solid rgba(126,226,188,.25);color:#7EE2BC;display:flex;align-items:center;justify-content:center}
.qx-card-badge .ql-svg{width:26px;height:26px}
.qx-card-title{font-size:clamp(22px,2.6vw,32px);font-weight:700;color:#fff;margin-top:22px}
.qx-card-text{font-size:16px;line-height:1.7;color:rgba(242,247,244,.65);margin:14px 0 0;max-width:420px}
.qx-card-step{display:inline-flex;align-items:center;gap:8px;margin-top:26px;font-family:'Unbounded',sans-serif;font-size:11px;font-weight:600;letter-spacing:.16em;text-transform:uppercase;color:rgba(126,226,188,.6)}
.qx-card-step i{width:34px;height:1.5px;background:rgba(126,226,188,.35)}
/* deck visuals */
.qx-vis-menu{width:100%;max-width:360px;display:flex;flex-direction:column;gap:10px}
.qx-vis-menu .ql-adm-row{margin-top:0}
.qx-vis-brand{width:100%;max-width:360px;display:flex;flex-direction:column;gap:18px;align-items:center}
.qx-brandprev{width:100%;height:120px;border-radius:18px;position:relative;overflow:hidden;background:url('/landing/cover.jpg') center/cover,linear-gradient(135deg,#C4573A,#5C2117);display:flex;align-items:flex-end;padding:14px}
.qx-brandprev::before{content:'';position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,.1) 30%,rgba(0,0,0,.7) 100%)}
.qx-brandprev .bp-logo{position:absolute;top:10px;left:10px;width:34px;height:34px;border-radius:10px;background:#FAF8F5;color:#A8442B;font-family:'Unbounded',sans-serif;font-weight:700;font-size:15px;display:flex;align-items:center;justify-content:center}
.qx-brandprev .bp-name{position:relative;font-family:'Unbounded',sans-serif;font-size:16px;font-weight:600;color:#fff}
.ql-swatches{display:flex;gap:10px;margin-top:0}
.ql-swatch{width:36px;height:36px;border-radius:50%;border:2.5px solid rgba(255,255,255,.85);box-shadow:0 4px 12px rgba(0,0,0,.3);animation:ql-bob 3.4s ease-in-out infinite}
.ql-swatch:nth-child(2){animation-delay:.3s}.ql-swatch:nth-child(3){animation-delay:.6s}.ql-swatch:nth-child(4){animation-delay:.9s}.ql-swatch:nth-child(5){animation-delay:1.2s}
.qx-vis-qr{position:relative;display:flex;flex-direction:column;align-items:center;gap:16px}
.qx-vis-qr .ql-qr{width:150px;height:150px;padding:14px;background:#fff;border-radius:18px;box-shadow:0 20px 50px rgba(0,0,0,.4)}
.qx-vis-qr .ql-qr i{background:#0B1B14}
.qx-pdfchip{display:inline-flex;align-items:center;gap:7px;font-family:'Unbounded',sans-serif;font-size:11px;font-weight:600;color:#7EE2BC;background:rgba(15,157,110,.16);border:1px dashed rgba(126,226,188,.45);border-radius:999px;padding:8px 15px;animation:ql-bob 3.4s ease-in-out infinite}
@media(max-width:960px){
  .qx-how{padding:90px 0 0}
  .qx-deck{height:auto}
  .qx-deck-pin{position:static;height:auto;display:flex;flex-direction:column;gap:18px;overflow:visible;padding:0 18px}
  .qx-card{width:100%;flex-direction:column;align-items:flex-start;gap:30px;padding:32px 26px}
  .qx-card-num{top:-14px;right:2px;font-size:64px}
  .qx-card-r{width:100%}
}

/* ============================ rail (features) ============================ */
.qx-feats{padding:150px 0 0}
.qx-rail{position:relative;height:300vh}
.qx-rail-pin{position:sticky;top:0;height:100vh;display:flex;align-items:center;overflow:hidden}
.qx-ghost{position:absolute;left:4%;top:7%;font-size:clamp(80px,13vw,190px);font-weight:800;line-height:1;color:transparent;-webkit-text-stroke:1.5px rgba(126,226,188,.08);pointer-events:none;user-select:none;white-space:nowrap}
.qx-rail-track{display:flex;align-items:stretch;gap:26px;padding:0 max(26px,calc((100vw - 1180px)/2 + 26px));will-change:transform}
.qx-panel{flex:none;width:min(520px,84vw);display:flex;will-change:transform}
.qx-panel-in{flex:1;display:flex;flex-direction:column;border-radius:30px;padding:40px;background:linear-gradient(160deg,rgba(255,255,255,.05),rgba(255,255,255,.015));border:1px solid rgba(255,255,255,.1);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);transition:border-color .3s}
.qx-panel:hover .qx-panel-in{border-color:rgba(126,226,188,.35)}
.qx-panel-top{display:flex;align-items:center;justify-content:space-between;gap:14px}
.qx-panel-num{font-family:'Unbounded',sans-serif;font-size:15px;font-weight:700;color:transparent;-webkit-text-stroke:1px rgba(126,226,188,.55)}
.qx-panel-icon{width:48px;height:48px;border-radius:15px;background:rgba(15,157,110,.14);border:1px solid rgba(126,226,188,.22);color:#7EE2BC;display:flex;align-items:center;justify-content:center}
.qx-panel-icon .ql-svg{width:24px;height:24px}
.qx-panel-title{font-size:22px;font-weight:700;color:#fff;margin-top:22px}
.qx-panel-text{font-size:15px;line-height:1.65;color:rgba(242,247,244,.62);margin-top:10px}
.qx-panel-mock{margin-top:auto;padding-top:26px}
@media(max-width:960px){
  .qx-feats{padding:90px 0 0}
  .qx-rail{height:auto}
  .qx-rail-pin{position:static;height:auto;overflow:visible;display:block}
  .qx-ghost{display:none}
  .qx-rail-track{overflow-x:auto;padding:0 18px 14px;scroll-snap-type:x mandatory;scrollbar-width:none}
  .qx-rail-track::-webkit-scrollbar{display:none}
  .qx-panel{scroll-snap-align:center}
  .qx-panel-in{min-height:0;padding:28px 24px}
  .qx-panel-mock{padding-top:22px}
}
/* browser mock with typing url (light window on dark panel) */
.ql-browser{border:1px solid rgba(255,255,255,.12);border-radius:14px;overflow:hidden;background:#FAF8F5}
.ql-browser-bar{display:flex;align-items:center;gap:8px;background:#fff;border-bottom:1px solid #ECE8E2;padding:9px 12px}
.ql-bdot{width:9px;height:9px;border-radius:50%}
.ql-bdot.r{background:#FF8A7A}.ql-bdot.y{background:#F5C36B}.ql-bdot.g{background:#4ADE80}
.ql-burl{flex:1;display:flex;align-items:center;background:#F3F0EB;border-radius:8px;padding:5px 10px;font-size:12px;color:#374151}
.ql-type{font-family:ui-monospace,'SF Mono',Menlo,monospace;display:inline-block;overflow:hidden;white-space:nowrap;max-width:0;border-right:2px solid #0F9D6E;animation:none}
.ql-reveal.in .ql-type,.qx-reveal.in .ql-type{animation:ql-type 2.6s steps(24) .5s forwards,ql-caret 1s step-end infinite}
.ql-burl .nm{color:#C4573A;font-weight:700}
.ql-browser-body{padding:16px;display:flex;gap:10px;align-items:center;flex-wrap:wrap}
.ql-bb-logo{width:34px;height:34px;border-radius:10px;background:linear-gradient(135deg,#C4573A,#8A3524);flex:none;display:flex;align-items:center;justify-content:center;color:#fff;font-family:'Unbounded',sans-serif;font-size:14px;font-weight:700}
.ql-bb-name{font-family:'Unbounded',sans-serif;font-size:13px;font-weight:600;color:#14201B;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ql-bb-line{height:9px;border-radius:5px;background:#ECE8E2}
.ql-url-hint{flex:none;font-family:'Unbounded',sans-serif;font-size:10.5px;font-weight:600;color:#0B7A55;background:rgba(15,157,110,.1);border:1px dashed rgba(15,157,110,.4);border-radius:999px;padding:6px 11px;white-space:nowrap;animation:ql-bob 3.4s ease-in-out infinite}
/* team + map mocks */
.ql-avatars{display:flex}
.ql-avatar{width:46px;height:46px;border-radius:50%;border:3px solid rgba(10,25,19,.9);margin-left:-12px;display:flex;align-items:center;justify-content:center;font-size:16px;font-weight:700;color:#fff;box-shadow:0 6px 16px rgba(0,0,0,.35);animation:ql-bob 3.6s ease-in-out infinite}
.ql-avatar:first-child{margin-left:0}
.ql-avatar:nth-child(2){animation-delay:.4s}.ql-avatar:nth-child(3){animation-delay:.8s}
.ql-map{position:relative;min-height:150px;border-radius:16px;overflow:hidden;background:radial-gradient(300px 160px at 60% 40%,rgba(15,157,110,.25),transparent 70%),#0C1A15;border:1px solid rgba(255,255,255,.08)}
.ql-map::before{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(255,255,255,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.05) 1px,transparent 1px);background-size:30px 30px}
.ql-pin{position:absolute;width:12px;height:12px;border-radius:50%;background:#34D399;box-shadow:0 0 0 rgba(52,211,153,.5);animation:ql-ping 2.4s ease-out infinite}
.ql-pin:nth-child(2){animation-delay:.5s}.ql-pin:nth-child(3){animation-delay:1s}.ql-pin:nth-child(4){animation-delay:1.5s}

/* ============================ admin showcase ============================ */
.qx-adminsec{padding:170px 0 0}
.qx-adminshell{position:relative;border-radius:32px;padding:40px;background:linear-gradient(160deg,#0B1E16,#06110C);border:1px solid rgba(255,255,255,.1);box-shadow:0 50px 130px rgba(0,0,0,.5);overflow:hidden}
.qx-adminshell::before{content:'';position:absolute;inset:0;background:radial-gradient(600px 300px at 85% -10%,rgba(15,157,110,.16),transparent 70%);pointer-events:none}
@media(max-width:640px){.qx-adminsec{padding:100px 0 0}.qx-adminshell{padding:20px 14px;border-radius:22px}}
.ql-admin-in{position:relative;display:grid;grid-template-columns:360px 1fr;gap:28px;align-items:center}
.ql-admin-tabs{display:flex;flex-direction:column;gap:12px}
.ql-admin-tab{position:relative;overflow:hidden;display:flex;gap:14px;padding:17px 18px;border:1px solid rgba(255,255,255,.1);border-radius:18px;background:rgba(255,255,255,.03);cursor:pointer;transition:background .25s,border-color .25s}
.ql-admin-tab:hover{background:rgba(255,255,255,.07)}
.ql-admin-tab.on{background:rgba(15,157,110,.13);border-color:rgba(126,226,188,.38)}
.ql-admin-tab .num{flex:none;font-size:15px;font-weight:700;color:transparent;-webkit-text-stroke:1px rgba(126,226,188,.6);line-height:1.5}
.ql-admin-tab .bd{min-width:0}
.ql-admin-tab .tt{font-size:15.5px;font-weight:700;color:#fff}
.ql-admin-tab .tx{font-size:13px;line-height:1.55;color:rgba(255,255,255,.55);margin-top:5px}
.ql-admin-tab.on .tx{color:rgba(255,255,255,.72)}
.ql-admin-tab .bar{position:absolute;left:0;right:0;bottom:0;height:2.5px;background:rgba(255,255,255,.08)}
.ql-admin-tab .bar i{position:absolute;left:0;top:0;bottom:0;width:0;background:linear-gradient(90deg,#0F9D6E,#7EE2BC);animation:ql-adm-bar 4s linear forwards}
.ql-admin-stage{min-width:0}
.ql-adm-frame{margin-top:0;background:#fff;border-color:rgba(255,255,255,.14);box-shadow:0 40px 90px rgba(0,0,0,.5)}
.ql-adm{display:flex;min-height:340px}
.ql-adm-mtop{display:none;align-items:center;gap:8px;padding:10px 12px;background:linear-gradient(90deg,#0E241B,#0A1B14)}
.ql-adm-mtop .mk{flex:none;width:24px;height:24px;border-radius:8px;background:linear-gradient(135deg,#12A574,#0B7A55);color:#fff;font-family:'Unbounded',sans-serif;font-size:10px;display:flex;align-items:center;justify-content:center}
.ql-adm-mtop .nm3{flex:1;min-width:0;font-family:'Playfair Display',Georgia,serif;font-size:13px;font-weight:700;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ql-adm-mtop .mb{flex:none;width:26px;height:26px;border-radius:8px;border:1px solid rgba(255,255,255,.22);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px}
.ql-adm-mtop .mb i{width:12px;height:1.5px;border-radius:1px;background:rgba(255,255,255,.8)}
.ql-adm-side{flex:none;width:158px;padding:13px 10px;display:flex;flex-direction:column;gap:3px;background:linear-gradient(180deg,#0E241B 0%,#0A1B14 100%)}
.ql-adm-brand{display:flex;align-items:center;gap:8px;padding:2px 3px;margin-bottom:10px}
.ql-adm-brand .mk{flex:none;width:28px;height:28px;border-radius:9px;background:linear-gradient(135deg,#12A574,#0B7A55);color:#fff;font-family:'Unbounded',sans-serif;font-size:12px;display:flex;align-items:center;justify-content:center}
.ql-adm-brand .bd{min-width:0}
.ql-adm-brand .nm3{font-family:'Playfair Display',Georgia,serif;font-size:13.5px;font-weight:700;color:#fff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ql-adm-brand .rl{font-size:9.5px;color:rgba(255,255,255,.55);margin-top:1px}
.ql-adm-open{display:flex;align-items:center;justify-content:center;gap:5px;font-size:10.5px;font-weight:700;color:#7EE2BC;background:rgba(15,157,110,.25);border-radius:8px;padding:6px 4px;margin-bottom:10px;white-space:nowrap}
.ql-adm-open .ql-svg{width:11px;height:11px;flex:none}
.ql-adm-nav{display:flex;align-items:center;gap:8px;font-size:11.5px;font-weight:600;color:rgba(255,255,255,.6);padding:8px 9px;border-radius:9px;transition:background .2s,color .2s;white-space:nowrap;overflow:hidden}
.ql-adm-nav .ql-svg{width:14px;height:14px;flex:none;color:rgba(255,255,255,.5)}
.ql-adm-nav span{overflow:hidden;text-overflow:ellipsis}
.ql-adm-nav.on{background:rgba(15,157,110,.22);color:#fff}
.ql-adm-nav.on .ql-svg{color:#7EE2BC}
.ql-adm-main{flex:1;min-width:0;padding:15px;background:radial-gradient(380px 150px at 88% -30px,rgba(15,157,110,.09),transparent 70%),#FAF8F5;animation:ql-rise-in .5s cubic-bezier(.22,.61,.36,1)}
.ql-adm-head{display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:12px;min-height:30px}
.ql-adm-title{font-family:'Playfair Display',Georgia,serif;font-size:16px;font-weight:700;color:#1F2421}
.ql-adm-btn{display:inline-flex;align-items:center;justify-content:center;font-size:11.5px;font-weight:700;color:#fff;background:linear-gradient(135deg,#0F9D6E,#0B7A55);border-radius:9px;padding:7px 12px;white-space:nowrap;box-shadow:0 6px 14px -6px rgba(15,157,110,.7)}
.ql-adm-btn.wide{width:100%;margin-top:14px;padding:10px}
.ql-adm-row{display:flex;align-items:center;gap:10px;border:1px solid #ECE8E2;border-radius:13px;padding:8px 10px;margin-top:8px;background:#fff;box-shadow:0 1px 4px rgba(31,36,33,.04);transition:opacity .3s,filter .3s}
.ql-adm-row .act{flex:none;width:24px;height:24px;border-radius:8px;background:#FAF8F5;border:1px solid #ECE8E2;display:inline-flex;align-items:center;justify-content:center;color:#9CA3AF}
.ql-adm-row .act .ql-svg{width:11px;height:11px}
.ql-adm-row .ql-mimg{width:38px;height:38px}
.ql-adm-row .bd{flex:1;min-width:0}
.ql-adm-row .nm2{font-size:12.5px;font-weight:600;color:#14201B;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ql-adm-row .sb{font-size:10.5px;color:#6B7280;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ql-adm-row .price{flex:none;font-size:11.5px;font-weight:800;color:#0B7A55;background:rgba(15,157,110,.1);border-radius:999px;padding:3px 9px;white-space:nowrap}
.ql-adm-row.off{opacity:.55;filter:grayscale(1)}
.ql-adm-row .stoptag{flex:none;font-size:10px;font-weight:700;color:#E05B4C;background:rgba(255,107,91,.12);border-radius:999px;padding:3px 8px;white-space:nowrap}
.ql-adm-sw{flex:none;width:34px;height:20px;border-radius:999px;background:#D6D2CB;position:relative;transition:background .25s}
.ql-adm-sw::after{content:'';position:absolute;top:2px;left:2px;width:16px;height:16px;border-radius:50%;background:#fff;box-shadow:0 1px 4px rgba(0,0,0,.2);transition:left .25s}
.ql-adm-sw.on{background:#0F9D6E}
.ql-adm-sw.on::after{left:16px}
.ql-adm-seg{display:inline-flex;gap:3px;background:#fff;border:1px solid #ECE8E2;border-radius:10px;padding:3px}
.ql-adm-seg span{font-size:10.5px;font-weight:700;color:#6B7280;border-radius:8px;padding:5px 10px;white-space:nowrap}
.ql-adm-seg span.on{color:#fff;background:linear-gradient(135deg,#0F9D6E,#0B7A55);box-shadow:0 6px 14px -6px rgba(15,157,110,.7)}
.ql-adm-cat{display:flex;align-items:center;gap:8px;margin-bottom:2px}
.ql-adm-cat .idx{flex:none;width:22px;height:22px;border-radius:7px;display:flex;align-items:center;justify-content:center;font-family:'Unbounded',sans-serif;font-size:9px;font-weight:700;color:#0B7A55;background:rgba(15,157,110,.1)}
.ql-adm-cat .nm{font-family:'Unbounded',sans-serif;font-size:12.5px;font-weight:700;color:#14201B;letter-spacing:-.02em}
.ql-adm-cat .cnt{font-size:10px;font-weight:600;color:#9CA3AF;background:#F5F2ED;border-radius:999px;padding:2px 8px}
.ql-adm-lanes{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;align-items:start}
.ql-adm-lane{border-radius:12px;padding:8px;border:1px solid}
.ql-adm-lane.new{background:rgba(138,143,152,.06);border-color:rgba(138,143,152,.16)}
.ql-adm-lane.cook{background:rgba(232,163,61,.07);border-color:rgba(232,163,61,.22)}
.ql-adm-lane.ready{background:rgba(15,157,110,.07);border-color:rgba(15,157,110,.2)}
.ql-adm-lane .lh{display:flex;align-items:center;gap:5px;margin-bottom:7px;min-width:0}
.ql-adm-lane .lh i{flex:none;width:8px;height:8px;border-radius:50%}
.ql-adm-lane.new .lh i{background:#8A8F98}
.ql-adm-lane.cook .lh i{background:#E8A33D}
.ql-adm-lane.ready .lh i{background:#0F9D6E}
.ql-adm-lane .lh span{font-size:10px;font-weight:700;color:#374151;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ql-adm-lane .lh .cnt{margin-left:auto;font-size:9px;color:#fff;border-radius:999px;padding:1px 6px;overflow:visible}
.ql-adm-lane.new .lh .cnt{background:#8A8F98}
.ql-adm-lane.cook .lh .cnt{background:#E8A33D}
.ql-adm-lane.ready .lh .cnt{background:#0F9D6E}
.ql-adm-ocard{background:#fff;border:1px solid #ECE8E2;border-radius:10px;padding:7px 8px;box-shadow:0 1px 4px rgba(31,36,33,.04)}
.ql-adm-ocard .tb{font-size:10px;font-weight:800;color:#14201B;margin-bottom:4px}
.ql-adm-ocard .it{font-size:9.5px;color:#6B7280;line-height:1.5;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ql-adm-tblbtns{display:flex;align-items:center;gap:6px}
.ql-adm-plus{width:26px;height:26px;border-radius:8px;background:#0F9D6E;color:#fff;display:inline-flex;align-items:center;justify-content:center}
.ql-adm-plus .ql-svg{width:13px;height:13px}
.ql-adm-btn2{display:inline-flex;align-items:center;gap:5px;font-size:10.5px;font-weight:700;color:#374151;background:#fff;border:1px solid #ECE8E2;border-radius:9px;padding:6px 10px;white-space:nowrap}
.ql-adm-btn2 .ql-svg{width:12px;height:12px;color:#0F9D6E}
.ql-adm-floor{position:relative;height:196px;border:1px solid #ECE8E2;border-radius:13px;background-color:#FAF8F5;background-image:radial-gradient(circle,#E5E0D8 1px,transparent 1px);background-size:20px 20px;overflow:hidden}
.ql-adm-tbl{position:absolute;width:54px;height:54px;transform:translate(-50%,-50%)}
.ql-adm-tbl .ch{position:absolute;background:rgba(31,36,33,.16);border-radius:3px}
.ql-adm-tbl .ch.n{left:50%;top:0;transform:translateX(-50%);width:13px;height:4px}
.ql-adm-tbl .ch.s{left:50%;bottom:0;transform:translateX(-50%);width:13px;height:4px}
.ql-adm-tbl .ch.w{left:0;top:50%;transform:translateY(-50%);width:4px;height:13px}
.ql-adm-tbl .ch.e{right:0;top:50%;transform:translateY(-50%);width:4px;height:13px}
.ql-adm-tbl .top{position:absolute;inset:6px;border-radius:50%;background:#EDE9E2;box-shadow:0 4px 12px rgba(31,36,33,.18);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px;font-size:12px;font-weight:800;color:#1F2421;line-height:1}
.ql-adm-tbl .top .ql-svg{width:10px;height:10px}
.ql-adm-tbl.call .ch,.ql-adm-tbl.cook .ch,.ql-adm-tbl.ready .ch{background:rgba(31,36,33,.4)}
.ql-adm-tbl.call .top{background:linear-gradient(135deg,#FF8A7A,#E05B4C);color:#fff;animation:ql-tbl-pulse 1.6s ease-in-out infinite}
.ql-adm-tbl.cook .top{background:linear-gradient(135deg,#F2B65A,#E8A33D);color:#7A4E12}
.ql-adm-tbl.ready .top{background:linear-gradient(135deg,#17B981,#0F9D6E);color:#fff}
@keyframes ql-tbl-pulse{0%,100%{box-shadow:0 4px 12px rgba(224,91,76,.35)}50%{box-shadow:0 0 0 8px rgba(224,91,76,.14)}}
.ql-adm-legend{display:flex;gap:12px;margin-top:9px;flex-wrap:wrap}
.ql-adm-legend .lg{display:inline-flex;align-items:center;gap:5px;font-size:9.5px;font-weight:600;color:#6B7280;white-space:nowrap}
.ql-adm-legend .lg i{width:8px;height:8px;border-radius:50%}
.ql-adm-legend .lg.call i{background:#E05B4C}
.ql-adm-legend .lg.cook i{background:#E8A33D}
.ql-adm-legend .lg.ready i{background:#0F9D6E}
.ql-adm-hero{display:flex;align-items:center;justify-content:space-between;gap:12px;border-radius:14px;padding:13px 14px;background:radial-gradient(300px 120px at 90% -30px,rgba(255,255,255,.16),transparent 70%),linear-gradient(135deg,#12A574 0%,#0B7A55 58%,#0A5C42 100%);box-shadow:0 12px 28px -12px rgba(11,122,85,.7)}
.ql-adm-hero .bd{min-width:0}
.ql-adm-hero .lb{font-size:8.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:rgba(255,255,255,.75)}
.ql-adm-hero .vl{display:flex;align-items:center;gap:8px;font-family:'Playfair Display',Georgia,serif;font-size:20px;font-weight:700;color:#fff;line-height:1.1;margin-top:3px;white-space:nowrap}
.ql-adm-hero .dl{font-family:system-ui,sans-serif;font-size:9.5px;font-weight:700;color:#D9FFEF;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.14);border-radius:999px;padding:3px 8px}
.ql-adm-hero .sp{flex:none;width:118px}
.ql-adm-hero .sp .ql-svg{width:100%}
.ql-adm-kpis{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-top:10px}
.ql-adm-kpi{display:flex;align-items:center;gap:9px;background:#fff;border:1px solid #ECE8E2;border-radius:13px;padding:10px;min-width:0}
.ql-adm-kpi .ic{flex:none;width:30px;height:30px;border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:14px}
.ql-adm-kpi .ic.amber{background:rgba(232,163,61,.14);color:#E8A33D}
.ql-adm-kpi .ic.coral{background:rgba(224,91,76,.12);color:#E05B4C}
.ql-adm-kpi .bd{min-width:0}
.ql-adm-kpi b{display:block;font-size:13px;color:#14201B;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ql-adm-kpi span{display:block;font-size:10px;color:#6B7280;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ql-adm-bars{display:flex;align-items:flex-end;gap:8px;height:88px;margin-top:10px;background:#fff;border:1px solid #ECE8E2;border-radius:13px;padding:12px}
.ql-adm-bars i{flex:1;border-radius:6px 6px 2px 2px;background:linear-gradient(180deg,#34D399,#0F9D6E);transform-origin:bottom;animation:ql-bar-in .9s cubic-bezier(.22,.61,.36,1) backwards}
.ql-adm-bars i:nth-child(2){animation-delay:.07s}.ql-adm-bars i:nth-child(3){animation-delay:.14s}.ql-adm-bars i:nth-child(4){animation-delay:.21s}.ql-adm-bars i:nth-child(5){animation-delay:.28s}.ql-adm-bars i:nth-child(6){animation-delay:.35s}.ql-adm-bars i:nth-child(7){animation-delay:.42s}
@media(max-width:960px){.ql-admin-in{grid-template-columns:1fr;gap:24px}.ql-admin-tabs{flex-direction:row;overflow-x:auto;gap:10px;padding-bottom:6px;scrollbar-width:none;touch-action:pan-x pan-y;-webkit-overflow-scrolling:touch;overscroll-behavior-x:contain}.ql-admin-tabs::-webkit-scrollbar{display:none}.ql-admin-tab{flex:none;width:220px;padding:13px 15px}.ql-admin-tab .tx{display:none}}
@media(max-width:640px){.ql-adm{flex-direction:column;min-height:0}.ql-adm-side{display:none}.ql-adm-mtop{display:flex}.ql-adm-floor{height:170px}}
@keyframes ql-adm-bar{to{width:100%}}
@keyframes ql-bar-in{from{transform:scaleY(.06)}to{transform:scaleY(1)}}

/* ============================ stats: the printing receipt ============================ */
.qx-stats{position:relative;z-index:1;margin-top:170px;padding:60px 0 40px}
.qx-print{max-width:560px;margin:0 auto;position:relative}
.qx-rslot{position:relative;z-index:3;height:18px;border-radius:999px;background:linear-gradient(180deg,#0D231A,#050E0A);border:1px solid rgba(255,255,255,.14);box-shadow:inset 0 3px 8px rgba(0,0,0,.7),0 10px 30px rgba(0,0,0,.4)}
.qx-rslot::after{content:'';position:absolute;left:14px;right:14px;top:7px;height:4px;border-radius:4px;background:#030806}
.qx-rwrap{overflow:hidden;margin:-2px 14px 0;padding-bottom:14px}
.qx-receipt{position:relative;background:#F7F5F0;color:#232823;border-radius:0 0 2px 2px;padding:30px 28px 30px;font-family:ui-monospace,'SF Mono',Menlo,monospace;box-shadow:0 34px 90px rgba(0,0,0,.55);transform:translateY(calc((1 - var(--pp,0)) * -96%)) rotate(calc((1 - var(--pp,0)) * -1deg));will-change:transform}
.qx-receipt::after{content:'';position:absolute;left:0;right:0;bottom:-9px;height:10px;background:linear-gradient(-45deg,#F7F5F0 6px,transparent 0) 0 0/14px 10px repeat-x,linear-gradient(45deg,#F7F5F0 6px,transparent 0) 7px 0/14px 10px repeat-x}
.qx-rhead{text-align:center;font-weight:700;font-size:16px;letter-spacing:.22em}
.qx-rsub{text-align:center;font-size:10.5px;color:#8B908A;margin-top:7px;letter-spacing:.1em}
.qx-rdiv{border-top:2px dashed #D8D4CC;margin:18px 0 6px}
.qx-rline{display:flex;align-items:baseline;gap:10px;font-size:12.5px;margin-top:13px;line-height:1.4}
.qx-rline .lb{color:#4A4F49;max-width:56%}
.qx-rline .dots{flex:1;border-bottom:2px dotted #C9C4BB;transform:translateY(-3px);min-width:20px}
.qx-rline .vl{font-weight:700;font-size:21px;white-space:nowrap;font-variant-numeric:tabular-nums;letter-spacing:.02em}
.qx-rbar{height:36px;margin-top:20px;background:repeating-linear-gradient(90deg,#232823 0 2px,transparent 2px 5px,#232823 5px 6px,transparent 6px 9px,#232823 9px 12px,transparent 12px 15px,#232823 15px 16px,transparent 16px 18px)}
.qx-rsite{display:block;text-align:center;font-size:10.5px;color:#8B908A;margin-top:10px;letter-spacing:.12em}
/* odometer digits inside receipt values */
.qx-odc{display:inline-block;overflow:hidden;height:1em;vertical-align:-0.08em}
.qx-ods{display:block;transition:transform 1.6s cubic-bezier(.19,.8,.22,1)}
.qx-ods i{display:block;height:1em;line-height:1;font-style:normal}
@media(max-width:760px){.qx-stats{margin-top:100px;padding:64px 0 74px}.qx-receipt{padding:24px 18px 26px}.qx-rline .vl{font-size:18px}}

/* ============================ reviews ============================ */
.qx-reviews{padding:150px 0 0}
.qx-revwrap{margin-top:-10px;display:flex;flex-direction:column;gap:22px;overflow:hidden;mask-image:linear-gradient(90deg,transparent,#000 7%,#000 93%,transparent);-webkit-mask-image:linear-gradient(90deg,transparent,#000 7%,#000 93%,transparent)}
.qx-revrow{display:flex;gap:22px;width:max-content;padding:4px 0}
.qx-revrow.a{animation:qx-rev 52s linear infinite}
.qx-revrow.b{animation:qx-rev 52s linear infinite reverse}
.qx-revwrap:hover .qx-revrow{animation-play-state:paused}
.qx-review{flex:none;width:min(400px,82vw);display:flex;flex-direction:column;background:linear-gradient(160deg,rgba(255,255,255,.055),rgba(255,255,255,.02));border:1px solid rgba(255,255,255,.1);border-radius:22px;padding:26px 24px}
.qx-review .stars{display:flex;gap:3px}
.qx-review .st{width:16px;height:16px;color:#F5C36B}
.qx-review .st.off{color:rgba(255,255,255,.18)}
.qx-review .st .ql-svg{width:100%;height:100%}
.qx-review .q{font-size:14.5px;line-height:1.7;color:rgba(242,247,244,.82);margin:14px 0 18px;flex:1;white-space:normal}
.qx-review .who{display:flex;align-items:center;gap:11px}
.qx-review .av{flex:none;width:40px;height:40px;border-radius:50%;object-fit:cover;background:rgba(255,255,255,.1)}
.qx-review .nm{font-size:14px;font-weight:700;color:#fff}
@media(max-width:640px){.qx-reviews{padding:100px 0 0}}

/* ============================ pricing ============================ */
@property --qla{syntax:'<angle>';initial-value:0deg;inherits:false}
.qx-pricing{padding:160px 0 0}
/* two cards (Free/Pro), centered */
.qx-plans{display:grid;grid-template-columns:repeat(2,minmax(0,380px));gap:22px;align-items:stretch;justify-content:center}
@media(max-width:900px){.qx-plans{grid-template-columns:1fr;max-width:440px;margin-left:auto;margin-right:auto}.qx-pricing{padding:100px 0 0}}
.qx-plan{position:relative;display:flex;flex-direction:column;background:linear-gradient(160deg,rgba(255,255,255,.05),rgba(255,255,255,.015));border:1px solid rgba(255,255,255,.1);border-radius:24px;padding:32px 28px;transition:transform .35s cubic-bezier(.22,.61,.36,1),border-color .3s,box-shadow .35s}
/* paper menus slammed onto the table one by one (the hot plan lands last) */
.qx-plans .qx-plan{opacity:0}
.qx-plans.in .qx-plan{opacity:1;animation:qx-slam .8s cubic-bezier(.3,1.2,.36,1) var(--d,0s) backwards}
.qx-plan:hover{transform:translateY(-6px);border-color:rgba(126,226,188,.3);box-shadow:0 26px 60px rgba(0,0,0,.4)}
.qx-plan.hot{background:linear-gradient(165deg,rgba(15,157,110,.2),rgba(7,21,16,.6));box-shadow:0 26px 80px rgba(15,157,110,.22)}
.qx-plan.hot::before{content:'';position:absolute;inset:0;border-radius:24px;padding:1.6px;background:conic-gradient(from var(--qla),#0F9D6E,#7EE2BC,#34D399,#FF8A7A,#0F9D6E);-webkit-mask:linear-gradient(#fff 0 0) content-box,linear-gradient(#fff 0 0);-webkit-mask-composite:xor;mask-composite:exclude;animation:ql-border-spin 5s linear infinite;pointer-events:none}
.qx-plan-name{font-size:15px;font-weight:600;color:rgba(242,247,244,.65)}
.qx-plan-price{display:flex;align-items:baseline;gap:6px;margin-top:12px}
.qx-plan-price b{font-family:'Unbounded',sans-serif;font-size:38px;font-weight:700;color:#fff}
.qx-plan-price span{font-size:14px;color:rgba(242,247,244,.45)}
.qx-plan-price .old{font-size:17px;font-weight:600;color:rgba(242,247,244,.4);text-decoration:line-through;margin-right:2px}
.qx-plan-total{min-height:19px;margin-top:6px;font-size:13.5px;color:rgba(242,247,244,.45)}
.qx-plan-save{display:inline-block;font-size:12px;font-weight:700;color:#7EE2BC;background:rgba(15,157,110,.18);border:1px solid rgba(126,226,188,.3);border-radius:999px;padding:4px 10px;margin-top:10px;width:fit-content}
.qx-plan-list{margin:22px 0;padding:0;list-style:none;display:flex;flex-direction:column;gap:11px;flex:1}
.qx-plan-list li{display:flex;align-items:baseline;gap:9px;font-size:14px;color:rgba(242,247,244,.75);line-height:1.45}
.qx-plan-list .lead{flex:1;border-bottom:2px dotted rgba(242,247,244,.16);transform:translateY(-3px);min-width:14px}
.qx-check{flex:none;width:18px;height:18px;border-radius:50%;background:rgba(15,157,110,.2);color:#7EE2BC;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:800;align-self:center}
.qx-plan-cta{width:100%;text-align:center;margin-top:auto}
.qx-pricing-note{text-align:center;font-size:13.5px;color:rgba(242,247,244,.4);margin-top:28px}

/* ============================ faq ============================ */
.qx-faqsec{padding:150px 0 0}
.qx-faqs{max-width:800px;margin:0 auto;display:flex;flex-direction:column;gap:12px}
.qx-faq{background:linear-gradient(160deg,rgba(255,255,255,.045),rgba(255,255,255,.015));border:1px solid rgba(255,255,255,.1);border-radius:18px;overflow:hidden;cursor:pointer;transition:border-color .25s,background .25s}
.qx-faq:hover{border-color:rgba(126,226,188,.35)}
.qx-faq.open{border-color:rgba(126,226,188,.45);background:rgba(15,157,110,.06)}
.qx-faq-q{display:flex;align-items:center;gap:16px;padding:20px 22px;font-size:16px;font-weight:600;color:#fff}
.qx-faq-idx{flex:none;font-family:'Unbounded',sans-serif;font-size:12px;font-weight:700;color:transparent;-webkit-text-stroke:1px rgba(126,226,188,.5)}
.qx-faq-q>span:nth-child(2){flex:1}
.qx-faq-plus{flex:none;width:30px;height:30px;border-radius:50%;background:rgba(15,157,110,.15);color:#7EE2BC;display:flex;align-items:center;justify-content:center;font-size:17px;font-weight:500;transition:transform .35s cubic-bezier(.22,.61,.36,1),background .25s,color .25s}
.qx-faq.open .qx-faq-plus{transform:rotate(45deg);background:#0F9D6E;color:#fff}
.qx-faq-a{display:grid;grid-template-rows:0fr;transition:grid-template-rows .4s cubic-bezier(.22,.61,.36,1)}
.qx-faq.open .qx-faq-a{grid-template-rows:1fr}
.qx-faq-a>div{overflow:hidden}
.qx-faq-a p{margin:0;padding:0 22px 20px 60px;font-size:14.5px;line-height:1.7;color:rgba(242,247,244,.62)}
@media(max-width:640px){.qx-faqsec{padding:100px 0 0}.qx-faq-q{padding:17px 18px;font-size:15px}.qx-faq-a p{padding:0 18px 17px 18px}}

/* ============================ final cta: interactive QR ============================ */
.qx-cta{position:relative;z-index:1;margin-top:160px;padding:170px 0;text-align:center;overflow:hidden}
.qx-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(700px 420px at 50% 55%,rgba(15,157,110,.16),transparent 70%)}
.qx-bigqr{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);display:grid;grid-template-columns:repeat(17,1fr);gap:7px;width:min(660px,140vw);aspect-ratio:1;opacity:.2;z-index:0}
.qx-bigqr i{border-radius:2.5px;background:#34D399;opacity:.75;transition:transform .7s cubic-bezier(.16,.66,.3,1),opacity .7s}
.qx-bigqr i.off{background:transparent}
.qx-cta-in{position:relative;z-index:2}
.qx-cta-kick{display:inline-flex;align-items:center;gap:8px;font-family:'Unbounded',sans-serif;font-size:11.5px;font-weight:600;letter-spacing:.2em;text-transform:uppercase;color:#7EE2BC;background:rgba(6,17,13,.72);backdrop-filter:blur(6px);border:1px solid rgba(126,226,188,.3);border-radius:999px;padding:9px 18px}
.qx-cta-h{font-size:clamp(32px,5vw,62px);font-weight:700;line-height:1.1;color:#fff;margin:26px 0 0;text-shadow:0 10px 60px rgba(5,14,10,.9)}
.qx-cta-sub{font-size:17px;color:rgba(242,247,244,.72);margin:18px auto 0;max-width:480px;line-height:1.65;text-shadow:0 6px 30px rgba(5,14,10,.9)}
.qx-cta-btns{display:flex;justify-content:center;gap:14px;flex-wrap:wrap;margin-top:36px}
@media(max-width:640px){.qx-cta{margin-top:100px;padding:120px 0}.qx-cta-btns .ql-btn{width:100%;justify-content:center}}

/* ============================ footer ============================ */
.qx-footer{position:relative;z-index:1;margin-top:40px;border-top:1px solid rgba(255,255,255,.08);padding:64px 0 40px;color:rgba(242,247,244,.6);overflow:hidden}
.qx-footer-in{display:flex;align-items:flex-start;justify-content:space-between;gap:30px;flex-wrap:wrap}
.qx-footer-brand{color:#fff}
.qx-footer-tag{font-size:13.5px;margin-top:10px;max-width:280px;line-height:1.6;color:rgba(242,247,244,.5)}
.qx-footer-links{display:flex;gap:26px;font-size:14px}
.qx-footer-links a{opacity:.75;transition:opacity .2s,color .2s;cursor:pointer}
.qx-footer-links a:hover{opacity:1;color:#7EE2BC}
.qx-footer-contacts{display:flex;flex-direction:column;gap:8px;font-size:14px}
.qx-footer-contacts-title{font-size:12px;letter-spacing:.08em;text-transform:uppercase;color:rgba(242,247,244,.4)}
.qx-footer-contacts a{opacity:.85;transition:opacity .2s}
.qx-footer-contacts a:hover{opacity:1}
.qx-footer-bottom{border-top:1px solid rgba(255,255,255,.08);margin-top:38px;padding-top:22px;display:flex;justify-content:space-between;align-items:center;gap:12px 24px;flex-wrap:wrap;font-size:12.5px;color:rgba(242,247,244,.38)}
.qx-footer-legal{display:flex;gap:18px;flex-wrap:wrap}
.qx-footer-legal a{opacity:.75;transition:opacity .2s}
.qx-footer-legal a:hover{opacity:1}
.qx-fword{margin-top:46px;text-align:center;font-size:clamp(52px,11vw,170px);font-weight:800;line-height:.9;color:transparent;-webkit-text-stroke:1.5px rgba(126,226,188,.1);user-select:none;pointer-events:none;white-space:nowrap;background:linear-gradient(90deg,rgba(52,211,153,.55) var(--fill,0%),transparent var(--fill,0%));-webkit-background-clip:text;background-clip:text}
@media(max-width:640px){.qx-footer{padding:44px 0 30px}.qx-footer-in{flex-direction:column;gap:26px}.qx-footer-links{flex-wrap:wrap;gap:12px 22px}.qx-footer-bottom{flex-direction:column;gap:6px}}

/* ============================ QR morph widget (chapter counter) ============================ */
.qx-morph{position:fixed;left:24px;bottom:24px;z-index:110;width:74px;height:74px;pointer-events:none;opacity:0;transition:opacity .6s}
html.qx-ready .qx-morph{opacity:.85}
.qx-morph i{position:absolute;left:0;top:0;width:8px;height:8px;border-radius:2px;background:#34D399;opacity:0;transition:transform .75s cubic-bezier(.22,.61,.36,1),opacity .75s;transition-delay:var(--md,0s);will-change:transform}
@media(max-width:960px){.qx-morph{display:none}}

/* ============================ cursor lamp ============================ */
.qx-lamp{position:fixed;left:0;top:0;z-index:2;width:900px;height:900px;margin:-450px 0 0 -450px;border-radius:50%;pointer-events:none;background:radial-gradient(circle,rgba(52,211,153,.07),transparent 62%);opacity:0;transition:opacity .5s}
html.ql-cursor-on .qx-lamp{opacity:1}

/* ============================ nav letter roll ============================ */
.qx-roll{display:inline-block;overflow:hidden;height:1.35em;line-height:1.35em;vertical-align:middle}
.qx-roll span{display:block;transition:transform .32s cubic-bezier(.22,.61,.36,1)}
.qx-roll .t2{color:#7EE2BC}
.ql-nav-link:hover .qx-roll span{transform:translateY(-100%)}

/* ============================ admin bell toast ============================ */
.qx-belltoast{position:absolute;top:20px;right:20px;z-index:6;display:flex;align-items:center;gap:8px;background:linear-gradient(135deg,#FF8A7A,#E05B4C);color:#fff;font-size:12.5px;font-weight:700;border-radius:13px;padding:10px 15px;box-shadow:0 14px 34px rgba(224,91,76,.45);opacity:0;transform:translateY(-10px);animation:qx-belltoast 12s ease 4s infinite;pointer-events:none;white-space:nowrap}
.qx-belltoast .ql-svg{width:15px;height:15px;transform-origin:50% 0;animation:qx-bellring 12s ease 4s infinite}
@media(max-width:640px){.qx-belltoast{top:12px;right:12px;font-size:11px;padding:8px 11px}}

/* ============================ "+1 scan" ticker pip ============================ */
.qx-scanpip{position:absolute;z-index:8;top:44px;display:inline-flex;align-items:center;gap:6px;background:rgba(8,20,16,.88);border:1px solid rgba(126,226,188,.45);color:#7EE2BC;font-size:12px;font-weight:700;border-radius:999px;padding:6px 12px;pointer-events:none;animation:qx-pip 2.1s ease forwards;white-space:nowrap}
.qx-scanpip::before{content:'';width:6px;height:6px;border-radius:50%;background:#34D399;box-shadow:0 0 8px rgba(52,211,153,.8)}

/* ---------- stickers (price-tag style) ---------- */
.ql-sticker{position:absolute;z-index:7;font-family:'Unbounded',sans-serif;font-size:12px;font-weight:600;padding:9px 14px;border-radius:12px;box-shadow:0 10px 24px rgba(0,0,0,.18);animation:ql-wobble 5s ease-in-out infinite;white-space:nowrap}
.ql-sticker.yellow{background:#FFD84D;color:#1F2421}
.ql-sticker.coral{background:#FF6B5B;color:#fff}
.ql-sticker.mint{background:#7EE2BC;color:#0B3B2A}

/* ---------- svg helper ---------- */
.ql-svg{display:inline-flex;align-items:center;justify-content:center;line-height:0}
.ql-svg svg{width:100%;height:100%;display:block}
.ql-display{font-family:'Unbounded',system-ui,sans-serif;letter-spacing:-0.02em}

/* ---------- scan progress line ---------- */
.ql-scanline{position:fixed;top:0;left:0;height:3px;width:var(--p,0%);z-index:400;background:linear-gradient(90deg,rgba(52,211,153,0),#34D399 60%);pointer-events:none}
.ql-scanline::after{content:'';position:absolute;right:-3px;top:-3px;width:9px;height:9px;border-radius:50%;background:#34D399;box-shadow:0 0 14px 3px rgba(52,211,153,.75)}

/* ---------- custom scanner cursor ---------- */
.ql-cursor-on .qx-root,.ql-cursor-on .qx-root *{cursor:none!important}
.ql-cursor-dot{position:fixed;z-index:501;width:7px;height:7px;border-radius:50%;background:#34D399;pointer-events:none;transform:translate(-50%,-50%);box-shadow:0 0 8px rgba(52,211,153,.7)}
.ql-cursor-ring{--c:#34D399;position:fixed;z-index:500;width:34px;height:34px;pointer-events:none;transform:translate(-50%,-50%);opacity:.9;transition:width .25s,height .25s,opacity .25s;background:linear-gradient(var(--c),var(--c)) left 0 top 0/11px 2px,linear-gradient(var(--c),var(--c)) left 0 top 0/2px 11px,linear-gradient(var(--c),var(--c)) right 0 top 0/11px 2px,linear-gradient(var(--c),var(--c)) right 0 top 0/2px 11px,linear-gradient(var(--c),var(--c)) left 0 bottom 0/11px 2px,linear-gradient(var(--c),var(--c)) left 0 bottom 0/2px 11px,linear-gradient(var(--c),var(--c)) right 0 bottom 0/11px 2px,linear-gradient(var(--c),var(--c)) right 0 bottom 0/2px 11px;background-repeat:no-repeat}
.ql-cursor-ring.hot{width:52px;height:52px;--c:#FF8A7A;opacity:1}

/* ============================ legal & compare pages (light theme kept) ============================ */
.ql-root{background:#FAF8F5;color:#1F2421;font-family:system-ui,-apple-system,'Segoe UI',sans-serif;overflow-x:clip;min-height:100vh}
.ql-root *{box-sizing:border-box}
.ql-root a{text-decoration:none;color:inherit}
.ql-wrap{width:100%;max-width:1120px;margin:0 auto;padding:0 24px}
.ql-legal{min-height:100vh}
.ql-legal-head{background:#081210;padding:18px 0}
.ql-legal-head .ql-logo{color:#fff}
.ql-legal-body{max-width:760px;margin:0 auto;padding:48px 0 80px}
.ql-legal-body h1{font-family:'Unbounded',sans-serif;font-size:clamp(26px,4vw,38px);font-weight:700;color:#14201B;letter-spacing:-0.02em;margin:0}
.ql-legal-body .upd{font-size:13px;color:#9CA3AF;margin-top:10px}
.ql-legal-body .intro{font-size:16px;line-height:1.7;color:#4B5563;margin:22px 0 0;padding:16px 18px;background:#fff;border:1px solid #ECE8E2;border-radius:14px}
.ql-legal-body h2{font-family:'Unbounded',sans-serif;font-size:17px;font-weight:600;color:#14201B;margin:34px 0 0;letter-spacing:-0.01em}
.ql-legal-body p{font-size:15px;line-height:1.75;color:#4B5563;margin:12px 0 0}
.ql-legal-body ul{margin:12px 0 0;padding-left:22px}
.ql-legal-body li{font-size:15px;line-height:1.75;color:#4B5563;margin-top:6px}
.ql-legal-links{display:flex;gap:20px;flex-wrap:wrap;margin-top:44px;padding-top:22px;border-top:1px solid #ECE8E2;font-size:14px;font-weight:600}
.ql-legal-links a{color:#0B7A55}
.ql-legal-links a:hover{text-decoration:underline}
.ql-legal-foot{margin-top:16px;font-size:12.5px;color:#9CA3AF}
.ql-cmp{max-width:920px}
.ql-cmp-scroll{overflow-x:auto;margin-top:14px;border:1px solid #ECE8E2;border-radius:16px;background:#fff}
.ql-cmp-table{width:100%;border-collapse:collapse;font-size:13.5px;line-height:1.5;min-width:760px}
.ql-cmp-table th{font-family:'Unbounded',sans-serif;font-size:12px;font-weight:600;color:#14201B;text-align:left;padding:14px 14px;border-bottom:2px solid #ECE8E2;white-space:nowrap}
.ql-cmp-table td{padding:12px 14px;border-bottom:1px solid #F3F0EB;color:#4B5563;vertical-align:top}
.ql-cmp-table tr:last-child td{border-bottom:none}
.ql-cmp-table .lbl{font-weight:700;color:#14201B;white-space:nowrap}
.ql-cmp-table th.hl{color:#0B7A55}
.ql-cmp-table td.hl{background:rgba(15,157,110,.06);color:#14201B;font-weight:600}

/* ============================ keyframes ============================ */
@keyframes ql-drift{from{transform:translate(0,0) scale(1)}to{transform:translate(60px,40px) scale(1.15)}}
@keyframes ql-hue{to{background-position:300% 0}}
@keyframes ql-shine{0%,55%{left:-80%}100%{left:130%}}
@keyframes ql-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
@keyframes ql-spin{to{transform:rotate(360deg)}}
@keyframes ql-blink{0%,100%{opacity:1}50%{opacity:.25}}
@keyframes ql-pulse-dot{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.7)}}
@keyframes ql-marquee{to{transform:translateX(-50%)}}
@keyframes ql-cell{to{opacity:1}}
@keyframes ql-rise-in{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
@keyframes ql-screen-shine{0%,60%{transform:translateX(-120%)}100%{transform:translateX(120%)}}
@keyframes ql-heart{0%,100%{transform:scale(1)}12%{transform:scale(1.25)}24%{transform:scale(1)}}
@keyframes ql-toggle{0%,40%{left:2px}55%,90%{left:14px}100%{left:2px}}
@keyframes ql-bob{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
@keyframes ql-ping{0%{box-shadow:0 0 0 0 rgba(52,211,153,.5)}70%{box-shadow:0 0 0 16px rgba(52,211,153,0)}100%{box-shadow:0 0 0 0 rgba(52,211,153,0)}}
@keyframes ql-border-spin{to{--qla:360deg}}
@keyframes ql-type{from{max-width:0}to{max-width:24ch}}
@keyframes ql-caret{0%,100%{border-color:transparent}50%{border-color:#0F9D6E}}
@keyframes ql-wobble{0%,100%{transform:rotate(var(--r,-6deg))}50%{transform:rotate(calc(var(--r,-6deg) + 4deg))}}
@keyframes qx-word{to{transform:translateY(0) rotate(0deg)}}
@keyframes qx-fadeup{to{opacity:1;transform:none}}
@keyframes qx-cell-pop{to{opacity:1;transform:scale(1)}}
@keyframes qx-laser{0%{top:4px;opacity:0}12%{opacity:1}88%{opacity:1}100%{top:calc(100% - 6px);opacity:0}}
@keyframes qx-hint{0%{transform:translateY(0);opacity:1}70%{transform:translateY(15px);opacity:0}100%{transform:translateY(0);opacity:0}}
@keyframes qx-bitfloat{from{transform:translate(0,0) rotate(0deg)}to{transform:translate(14px,-22px) rotate(14deg)}}
@keyframes qx-rev{to{transform:translateX(-50%)}}
@keyframes qx-chapscan{0%{opacity:0;transform:translateY(0)}10%{opacity:1}85%{opacity:1}100%{opacity:0;transform:translateY(170px)}}
@keyframes qx-slam{0%{opacity:0;transform:translateY(-74px) rotate(-6deg) scale(1.07)}62%{opacity:1;transform:translateY(5px) rotate(1.2deg) scale(.985)}100%{opacity:1;transform:none}}
@keyframes qx-belltoast{0%,3%{opacity:0;transform:translateY(-10px)}7%,25%{opacity:1;transform:none}30%,100%{opacity:0;transform:translateY(-10px)}}
@keyframes qx-bellring{7%{transform:rotate(0)}9%{transform:rotate(16deg)}11%{transform:rotate(-13deg)}13%{transform:rotate(9deg)}15%{transform:rotate(-5deg)}17%{transform:rotate(0)}}
@keyframes qx-pip{0%{opacity:0;transform:translateY(8px) scale(.8)}14%{opacity:1;transform:none}68%{opacity:1}100%{opacity:0;transform:translateY(-52px)}}

/* ============================ mobile polish ============================ */
@media(max-width:640px){
  .qx-wrap{padding:0 18px}
  /* The mock stays first (it sells the product) but shrinks so the H1 and the
     primary CTA land inside the first viewport — on ad traffic most visitors
     never scrolled past a full-height mock with no visible headline or button.
     Height cap (not min-height) so the layout box matches the scaled pixels. */
  .qx-hero{padding:78px 0 56px}
  .qx-hero-in{gap:12px}
  .qx-h1{font-size:clamp(27px,8.2vw,34px)}
  .qx-hero-sub{font-size:15.5px}
  .qx-hero-ctas{margin-top:18px}
  .qx-hero-ctas .ql-btn,.qx-hero-ctas .ql-btn-ghost{width:100%;justify-content:center;text-align:center}
  .qx-hero-note{gap:10px 16px}
  .ql-hero-visual{min-height:0;height:400px;transform:scale(.66);transform-origin:top center}
  .qx-chap-title{margin-top:12px}
  .qx-card-title{margin-top:18px}
  .qx-stat-label{font-size:12.5px}
  .qx-fword{margin-top:30px}
}

@media (prefers-reduced-motion:reduce){
  .qx-root *,.qx-root *::before,.qx-root *::after{animation-duration:.01ms!important;animation-iteration-count:1!important;transition-duration:.01ms!important}
  .qx-reveal,.ql-reveal{opacity:1;transform:none}
  .qx-mask .w{transform:none}
  .qx-fade{opacity:1;transform:none}
  .qx-intro{display:none}
  .ql-type{max-width:24ch}
  .qx-plans .qx-plan{opacity:1;animation:none}
  .qx-morph,.qx-scanpip,.qx-lamp,.qx-belltoast,.qx-thread-wrap{display:none}
  .qx-receipt{transform:none}
  .qx-chap.in::after{animation:none}
  [data-flow]{opacity:1!important;transform:none!important}
  .qx-bandword{transform:none!important}
}
"""

// language=JavaScript
private const val LANDING_JS = """
(function(){
  function boot(){
    var doc=document;
    var reduced=window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var fine=window.matchMedia('(pointer:fine)').matches;

    /* ---------- intro scan: owned by the head-injected ql-intro-js ---------- */
    /* The head boot runs while app.js is still downloading and hides the intro
       via html.qx-intro-done, which survives hydration replacing the overlay
       node. This fallback only covers a document that lost the head script. */
    if(!window.qxIntroBooted){
      window.qxIntroBooted=true;
      doc.documentElement.classList.add('qx-ready');
      doc.documentElement.classList.add('qx-intro-done');
    }

    /* ---------- odometer count-up: each digit rolls on its own strip ---------- */
    var counters=new WeakSet();
    function countUp(el){
      if(counters.has(el))return;counters.add(el);
      var target=el.getAttribute('data-count')||'';
      var suffix=el.getAttribute('data-suffix')||'';
      if(!target)return;
      if(reduced){el.textContent=target+suffix;return;}
      el.textContent='';
      for(var i=0;i<target.length;i++){
        var ch=target.charAt(i);
        if(ch<'0'||ch>'9'){
          var lit=doc.createElement('span');lit.textContent=ch;el.appendChild(lit);continue;
        }
        var col=doc.createElement('span');col.className='qx-odc';
        var strip=doc.createElement('span');strip.className='qx-ods';
        for(var d=0;d<=9;d++){var dd=doc.createElement('i');dd.textContent=String(d);strip.appendChild(dd);}
        col.appendChild(strip);el.appendChild(col);
        (function(s,dig,dl){
          requestAnimationFrame(function(){requestAnimationFrame(function(){
            s.style.transitionDelay=dl+'ms';
            s.style.transform='translateY(-'+(dig*10)+'%)';
          });});
        })(strip,parseInt(ch,10),i*160+100);
      }
      if(suffix){var sf=doc.createElement('span');sf.textContent=suffix;el.appendChild(sf);}
    }

    /* ---------- reveal on scroll ---------- */
    var io=new IntersectionObserver(function(entries){
      entries.forEach(function(en){
        if(!en.isIntersecting)return;
        en.target.classList.add('in');
        en.target.querySelectorAll('[data-count]').forEach(countUp);
        if(en.target.hasAttribute('data-count'))countUp(en.target);
        io.unobserve(en.target);
      });
    },{threshold:0.12,rootMargin:'0px 0px -40px 0px'});
    function observeAll(){
      doc.querySelectorAll('.qx-reveal:not(.in),.ql-reveal:not(.in),[data-count]:not(.in)').forEach(function(el){io.observe(el)});
    }
    observeAll();

    /* ---------- stable chrome elements (outside the language-keyed subtree) ---------- */
    var dot=doc.getElementById('ql-cursor-dot');
    var ring=doc.getElementById('ql-cursor-ring');
    var lamp=doc.querySelector('.qx-lamp');
    var header=doc.getElementById('qx-header');
    var scan=doc.getElementById('qx-scanline');

    /* ---------- node cache for the scroll engine (rebuilt after language switches) ---------- */
    var deck=null,deckCards=[],rail=null,railTrack=null,railPanels=[],pars=[],bigqr=null,bigCells=[];
    var receipt=null,rwrap=null,fword=null,ctaEl=null,chapNums=[],heroMasks=[];
    var morphEl=null,morphCells=[];
    var morphSt=-1;
    var blobWrap=null,sceneBlobs=[],gridEl=null,flowItems=[],bands=[],heroVis=null;
    var threadPath=null,threadTip=null,threadLen=0,builtH=-1,builtW=-1;
    var spots=[],glowCells=[],hero=null,phoneW=null,mqTrack=null;
    var velS=0,prevY2=window.scrollY;
    var dirty=true;
    function collect(){
      deck=doc.getElementById('qx-deck');
      deckCards=deck?[].slice.call(deck.querySelectorAll('.qx-card')):[];
      rail=doc.getElementById('qx-rail');
      railTrack=doc.getElementById('qx-rail-track');
      railPanels=railTrack?[].slice.call(railTrack.querySelectorAll('.qx-panel')):[];
      pars=[].slice.call(doc.querySelectorAll('[data-par]'));
      bigqr=doc.getElementById('qx-bigqr');
      bigCells=bigqr?[].slice.call(bigqr.children).filter(function(c){return c.className.indexOf('off')<0}):[];
      receipt=doc.getElementById('qx-receipt');
      rwrap=doc.getElementById('qx-rwrap');
      fword=doc.querySelector('.qx-fword');
      ctaEl=doc.querySelector('.qx-cta');
      heroMasks=[].slice.call(doc.querySelectorAll('.qx-h1 .qx-mask'));
      chapNums=[].slice.call(doc.querySelectorAll('.qx-chap-num')).map(function(el){
        return {el:el,n:parseInt(el.textContent,10)||0};
      });
      morphEl=doc.getElementById('qx-morph');
      morphCells=morphEl?[].slice.call(morphEl.children):[];
      blobWrap=doc.getElementById('qx-scene-blobs');
      sceneBlobs=blobWrap?[].slice.call(blobWrap.children):[];
      gridEl=doc.getElementById('qx-scene-grid');
      flowItems=[].slice.call(doc.querySelectorAll('[data-flow]'));
      bands=[].slice.call(doc.querySelectorAll('.qx-bandword'));
      heroVis=doc.querySelector('.ql-hero-visual');
      threadPath=doc.getElementById('qx-thread-path');
      threadTip=doc.getElementById('qx-thread-tip');
      spots=[].slice.call(doc.querySelectorAll('.qx-spot'));
      var glowCard=doc.querySelector('.ql-fc-qr');
      glowCells=glowCard?[].slice.call(glowCard.querySelectorAll('.ql-qr i')).filter(function(c){
        return (c.getAttribute('style')||'').indexOf('none')<0;
      }):[];
      hero=doc.getElementById('qx-hero');
      phoneW=doc.getElementById('ql-phone-wrap');
      mqTrack=doc.querySelector('.ql-mq-track');
      builtH=-1;
      dirty=false;
    }

    /* ---------- the scan thread: build a smooth path through the chapter anchors ---------- */
    function buildThread(){
      var svg=doc.getElementById('qx-thread');
      if(!svg||!threadPath)return;
      var root=doc.querySelector('.qx-root');
      var H=root?root.scrollHeight:doc.documentElement.scrollHeight;
      var W=window.innerWidth;
      svg.setAttribute('viewBox','0 0 '+W+' '+H);
      svg.setAttribute('width',String(W));
      svg.setAttribute('height',String(H));
      var y0=window.scrollY;
      var pts=[];
      function add(el,dx,dy){
        if(!el)return;
        var r=el.getBoundingClientRect();
        pts.push([r.left+r.width/2+(dx||0),r.top+y0+r.height/2+(dy||0)]);
      }
      add(doc.querySelector('.qx-hero-ctas'),60,90);
      doc.querySelectorAll('.qx-chap-num').forEach(function(el){add(el,30,6);});
      add(doc.getElementById('qx-receipt'),0,-30);
      add(doc.querySelector('.qx-cta-kick'),0,6);
      pts.sort(function(a,b){return a[1]-b[1]});
      if(pts.length<2)return;
      var d='M '+pts[0][0].toFixed(1)+' '+pts[0][1].toFixed(1);
      for(var i=1;i<pts.length;i++){
        var a=pts[i-1],b=pts[i],midY=(a[1]+b[1])/2;
        var swing=(i%2?1:-1)*Math.min(W*0.3,440);
        d+=' C '+(a[0]+swing).toFixed(1)+' '+midY.toFixed(1)+' '+(b[0]-swing).toFixed(1)+' '+midY.toFixed(1)+' '+b[0].toFixed(1)+' '+b[1].toFixed(1);
      }
      threadPath.setAttribute('d',d);
      threadLen=threadPath.getTotalLength();
      threadPath.style.strokeDasharray=threadLen+' '+threadLen;
      threadPath.style.strokeDashoffset=String(threadLen);
      builtH=H;
      builtW=W;
    }

    /* scene waypoints: blob [x vw, y vh, scale] ×3 + hue shift, sampled by page progress */
    var WPS=[
      [[-8,-8,1.25],[82,6,1],[40,95,0.8],0],
      [[70,18,1.1],[6,55,1.25],[90,85,0.85],14],
      [[16,78,1.3],[86,28,0.95],[52,6,1.05],26],
      [[80,70,1.2],[12,18,1.05],[62,48,1.1],8],
      [[46,26,1.45],[16,82,1.05],[88,58,0.95],0]
    ];
    /* Ignore mutations from the animation chrome itself (cursor ring "hot",
       header "scrolled", scan pips, admin auto-tabs): otherwise every hover
       re-collected the whole cache and rebuilt the thread — the main jank source. */
    new MutationObserver(function(muts){
      for(var i=0;i<muts.length;i++){
        var t=muts[i].target;
        if(t===ring||t===dot||t===header)continue;
        if(t&&t.id==='ql-phone-wrap')continue;
        if(t&&t.closest&&t.closest('.ql-admin-in'))continue;
        observeAll();
        dirty=true;
        return;
      }
    }).observe(doc.body,{childList:true,subtree:true,attributes:true,attributeFilter:['class']});

    /* ---------- pointer: record position; heavy work happens once per frame ---------- */
    var pmx=-10000,pmy=-10000,pmoved=false;
    doc.addEventListener('pointermove',function(ev){
      pmx=ev.clientX;pmy=ev.clientY;pmoved=true;
      if(fine&&dot&&ring&&!reduced){
        dot.style.left=pmx+'px';dot.style.top=pmy+'px';
        var t=ev.target.closest?ev.target.closest('a,button,.qx-faq,.ql-admin-tab'):null;
        ring.classList.toggle('hot',!!t);
      }
    },{passive:true});
    if(fine&&dot&&ring&&!reduced)doc.documentElement.classList.add('ql-cursor-on');

    /* ---------- magnetic buttons (delegated, survives re-renders) ---------- */
    if(fine){
      var mag=null;
      doc.addEventListener('pointerover',function(ev){
        var b=ev.target.closest?ev.target.closest('.ql-btn,.ql-btn-ghost,.ql-btn-outline'):null;
        if(b)mag=b;
      },{passive:true});
      doc.addEventListener('pointermove',function(ev){
        if(!mag)return;
        if(!mag.isConnected){mag=null;return;}
        var r=mag.getBoundingClientRect();
        if(ev.clientX<r.left-30||ev.clientX>r.right+30||ev.clientY<r.top-30||ev.clientY>r.bottom+30){
          mag.style.setProperty('--tx','0px');
          mag.style.setProperty('--ty','0px');
          mag=null;
          return;
        }
        var dx=ev.clientX-(r.left+r.width/2);
        var dy=ev.clientY-(r.top+r.height/2);
        mag.style.setProperty('--tx',(dx*0.18)+'px');
        mag.style.setProperty('--ty',(dy*0.32)+'px');
      },{passive:true});
    }

    /* ---------- marquee reacts to scroll velocity and direction ---------- */
    var mqX=0,mqVel=0,mqDir=1,mqLastY=window.scrollY,mqHalf=0;
    function mqMeasure(){
      var track=mqTrack||doc.querySelector('.ql-mq-track');
      mqHalf=track?track.scrollWidth/2:0;
    }
    mqMeasure();
    window.addEventListener('resize',mqMeasure);
    window.addEventListener('scroll',function(){
      var y=window.scrollY,d=y-mqLastY;mqLastY=y;
      mqVel=Math.min(Math.abs(mqVel)+Math.abs(d)*0.045,5);
      if(d>0)mqDir=1;else if(d<0)mqDir=-1;
    },{passive:true});

    /* ---------- QR morph widget: cells reassemble into the chapter number ---------- */
    var MORPH_QR='1110111'+'1010101'+'1101011'+'0110110'+'1101011'+'1010101'+'1110111';
    var DIGS=['111101101101111','010110010010111','111001111100111','111001111001111','101101111001001','111100111001111','111100111101111'];
    function morphTo(st){
      if(!morphEl||!morphCells.length)return;
      var u=74/7;
      function place(c,gx,gy,on,idx){
        c.style.transform='translate('+(gx*u).toFixed(1)+'px,'+(gy*u).toFixed(1)+'px)';
        c.style.opacity=on?'1':'0';
        c.style.setProperty('--md',((idx%11)*26)+'ms');
      }
      if(st<1||st>6){
        morphCells.forEach(function(c,i){place(c,i%7,Math.floor(i/7),MORPH_QR.charAt(i)==='1',i);});
        return;
      }
      var bits=[];
      var d0=DIGS[0],dn=DIGS[st];
      var r,c2;
      for(r=0;r<5;r++)for(c2=0;c2<3;c2++){if(d0.charAt(r*3+c2)==='1')bits.push([c2,r+1]);}
      for(r=0;r<5;r++)for(c2=0;c2<3;c2++){if(dn.charAt(r*3+c2)==='1')bits.push([c2+4,r+1]);}
      morphCells.forEach(function(c,i){
        if(i<bits.length)place(c,bits[i][0],bits[i][1],true,i);
        else place(c,(i*3)%7,(i*5)%7,false,i);
      });
    }

    /* ---------- main rAF engine: batched reads → writes, smoothed progress ---------- */
    var vh=window.innerHeight;
    window.addEventListener('resize',function(){vh=window.innerHeight;dirty=true;});
    var wasDesk=null,lastTs=null;
    var sP=0,sQ=0,sRp=0,sPc=0,sPP=0,sMy=0,lastHue=999,lastDash=-1;
    var crx=-100,cry=-100,clx=-1000,cly=-1000;
    var repelWas=false;
    function clamp(v,a,b){return v<a?a:(v>b?b:v)}
    function ease3(p){return 1-Math.pow(1-p,3)}
    function lp(c,t,f){return c+(t-c)*f}
    function rb(el){return el.getBoundingClientRect()}
    function stl(el,p,v){if(!el._qs)el._qs={};if(el._qs[p]!==v){el._qs[p]=v;el.style[p]=v;}}
    function stv(el,p,v){if(!el._qv)el._qv={};if(el._qv[p]!==v){el._qv[p]=v;el.style.setProperty(p,v);}}
    function frame(ts){
      if(dirty)collect();
      if(lastTs==null)lastTs=ts;
      var dt=Math.min((ts-lastTs)/16.7,3);lastTs=ts;
      var f1=1-Math.pow(0.86,dt);
      var f2=1-Math.pow(0.78,dt);
      var y=window.scrollY;
      var desk=window.innerWidth>960&&!reduced;
      var doPtr=pmoved&&fine&&!reduced;
      if(desk!==wasDesk){
        deckCards.forEach(function(c){c.style.transform='';c.style.filter='';c.style.opacity='';c.style.zIndex='';});
        if(railTrack)railTrack.style.transform='';
        railPanels.forEach(function(p){p.style.transform='';});
        pars.forEach(function(p){p.style.transform='';p._qpy=0;});
        heroMasks.forEach(function(m){m.style.transform='';});
        if(heroVis)heroVis.style.transform='';
        bands.forEach(function(b){b.style.transform='';b._qbx=null;});
        wasDesk=desk;
      }

      /* -------- read phase: every rect this frame needs, before any write -------- */
      var de=doc.documentElement,docH=de.scrollHeight;
      if(desk&&threadPath&&(threadLen===0||Math.abs(docH-builtH)>3||window.innerWidth!==builtW)){
        buildThread();
        lastDash=-1;
      }
      var deckR=(desk&&deck&&deckCards.length)?rb(deck):null;
      var railR=(desk&&rail&&railTrack)?rb(rail):null;
      var railMaxX=railR?(railTrack.scrollWidth-(railTrack.parentElement?railTrack.parentElement.clientWidth:window.innerWidth)):0;
      var panelRs=(railR&&railR.bottom>0&&railR.top<vh)?railPanels.map(rb):null;
      var parRs=desk?pars.map(rb):null;
      var flowRs=flowItems.map(rb);
      var bandRs=desk?bands.map(rb):null;
      var bigR=(desk&&bigqr)?rb(bigqr):null;
      var chapTops=desk?chapNums.map(function(c){return rb(c.el).top}):null;
      var ctaTop=(desk&&ctaEl)?rb(ctaEl).top:1e9;
      var rwrapTop=rwrap?rb(rwrap).top:1e9;
      var heroR=(desk&&hero&&y<vh*1.5)?rb(hero):null;
      var spotRs=doPtr?spots.map(rb):null;
      var glowRs=(doPtr&&heroR&&heroR.bottom>0&&glowCells.length)?glowCells.map(rb):null;
      var repelNear=fine&&!reduced&&bigR&&bigR.bottom>0&&bigR.top<vh&&pmx>bigR.left-150&&pmx<bigR.right+150&&pmy>bigR.top-150&&pmy<bigR.bottom+150;
      var repelRs=(repelNear||repelWas)?bigCells.map(rb):null;

      /* -------- write phase -------- */
      velS+=((y-prevY2)-velS)*Math.min(1,0.12*dt);
      prevY2=y;
      if(header){
        header.classList.toggle('scrolled',y>14);
      }
      if(scan){
        var smax=docH-de.clientHeight;
        stv(scan,'--p',(smax>0?((y/smax)*100).toFixed(2):'0')+'%');
      }
      var dh=docH-vh;
      sP=lp(sP,dh>0?clamp(y/dh,0,1):0,f1);
      var P=sP;
      /* shared scene: blobs drift, breathe when idle, re-hue with progress */
      if(sceneBlobs.length===3&&!reduced){
        var seg=P*(WPS.length-1),si=Math.min(WPS.length-2,Math.floor(seg)),sf2=seg-si;
        var A=WPS[si],B=WPS[si+1];
        for(var bi=0;bi<3;bi++){
          var av=A[bi],bv=B[bi];
          var xo=Math.sin(ts*0.00012+bi*2.1)*1.6;
          var yo=Math.cos(ts*0.0001+bi*1.7)*1.4;
          sceneBlobs[bi].style.transform='translate3d('+(av[0]+(bv[0]-av[0])*sf2+xo).toFixed(2)+'vw,'+(av[1]+(bv[1]-av[1])*sf2+yo).toFixed(2)+'vh,0) scale('+(av[2]+(bv[2]-av[2])*sf2).toFixed(3)+')';
        }
        var hu=A[3]+(B[3]-A[3])*sf2;
        if(blobWrap&&Math.abs(hu-lastHue)>0.5){lastHue=hu;blobWrap.style.filter='hue-rotate('+hu.toFixed(1)+'deg)';}
        if(gridEl)stl(gridEl,'transform','translate3d(0,'+(-(y*0.08)%56).toFixed(1)+'px,0)');
      }
      /* the thread draws itself (quantized: slow scroll repaints less) */
      if(desk&&threadPath&&threadLen>0){
        var dOff=Math.round(threadLen*(1-P)/3)*3;
        if(dOff!==lastDash){
          lastDash=dOff;
          threadPath.style.strokeDashoffset=String(dOff);
          if(threadTip){
            var tpt=threadPath.getPointAtLength(clamp(threadLen-dOff,0,threadLen));
            threadTip.setAttribute('transform','translate('+tpt.x.toFixed(1)+' '+tpt.y.toFixed(1)+')');
          }
        }
      }
      /* scroll-choreographed blocks: smoothed enter + velocity skew */
      var sk=desk?Math.max(-2.2,Math.min(2.2,velS*0.05)):0;
      flowItems.forEach(function(el,i){
        var applied=el._qft||0;
        var fr=flowRs[i];
        var fp=clamp(1-((fr.top-applied)-vh*0.12)/(vh*0.72),0,1);
        var cur=el._qfe==null?fp:lp(el._qfe,fp,f2);
        el._qfe=cur;
        var fe=ease3(cur);
        var fty=(1-fe)*72;
        el._qft=fty;
        var tf='translate3d(0,'+fty.toFixed(1)+'px,0) scale('+(0.955+0.045*fe).toFixed(3)+')';
        if(Math.abs(sk)>0.06)tf+=' skewY('+sk.toFixed(2)+'deg)';
        stl(el,'transform',tf);
        stl(el,'opacity',Math.min(1,fe*1.2).toFixed(2));
      });
      /* giant word bands drift sideways */
      if(bandRs){
        bands.forEach(function(el,i){
          var brc=bandRs[i];
          if(brc.bottom<-200||brc.top>vh+200)return;
          var tgt=-(brc.top+brc.height/2-vh/2)*0.35;
          var bx=el._qbx==null?tgt:lp(el._qbx,tgt,f2);
          el._qbx=bx;
          stl(el,'transform','translate3d('+bx.toFixed(1)+'px,0,0)');
        });
      }
      /* final QR slowly rotates and grows */
      if(bigR&&bigR.bottom>-100&&bigR.top<vh+100){
        sPc=lp(sPc,clamp((vh-bigR.top)/(vh+bigR.height),0,1),f2);
        stl(bigqr,'transform','translate(-50%,-50%) rotate('+((sPc-0.5)*10).toFixed(2)+'deg) scale('+(0.88+0.24*sPc).toFixed(3)+')');
      }
      /* pinned deck of steps */
      if(deckR){
        var dtot=deckR.height-vh;
        sQ=lp(sQ,(dtot>0?clamp(-deckR.top/dtot,0,1):0)*(deckCards.length-1),f2);
        var q=sQ,n=deckCards.length;
        deckCards.forEach(function(c,i){
          var enter=i===0?1:clamp(q-(i-1),0,1);
          var exit=i>=n-1?0:clamp(q-i,0,1);
          var e=ease3(enter);
          stl(c,'transform','translate3d(0,'+((1-e)*vh*0.92).toFixed(1)+'px,0) rotate('+((1-e)*5-exit*1.5).toFixed(2)+'deg) scale('+(1-exit*0.07).toFixed(3)+')');
          stl(c,'filter','brightness('+(1-exit*0.45).toFixed(3)+')');
          stl(c,'opacity',enter<0.02?'0':'1');
          stl(c,'zIndex',String(10+i));
        });
      }
      /* horizontal rail + coverflow tilt */
      if(railR){
        var rtot=railR.height-vh;
        sRp=lp(sRp,rtot>0?clamp(-railR.top/rtot,0,1):0,f2);
        if(railMaxX>0)stl(railTrack,'transform','translate3d('+(-sRp*railMaxX).toFixed(1)+'px,0,0)');
        if(panelRs){
          railPanels.forEach(function(pn,i){
            var pr2=panelRs[i];
            var cc=(pr2.left+pr2.width/2-window.innerWidth/2)/window.innerWidth;
            stl(pn,'transform','perspective(1200px) rotateY('+Math.max(-10,Math.min(10,-cc*9)).toFixed(2)+'deg)');
          });
        }
      }
      /* parallax */
      if(parRs){
        pars.forEach(function(el,i){
          var applied=el._qpy||0;
          var pr=parRs[i];
          var t=-(((pr.top-applied)+pr.height/2-vh/2)/vh)*(parseFloat(el.getAttribute('data-par'))||0);
          el._qpy=t;
          stl(el,'transform','translate3d(0,'+t.toFixed(1)+'px,0)');
        });
      }
      /* hero words drift apart; the phone lags behind */
      if(desk&&heroMasks.length&&y<vh*1.4){
        sMy=lp(sMy,y,f2);
        heroMasks.forEach(function(m,i){
          var f=((i*7)%5+2)/3;
          stl(m,'transform','translate3d(0,'+(-sMy*f*0.22).toFixed(1)+'px,0) rotate('+((i%2?1:-1)*sMy*0.005*f).toFixed(2)+'deg)');
        });
        if(heroVis)stl(heroVis,'transform','translate3d(0,'+(sMy*0.1).toFixed(1)+'px,0)');
      }
      /* the stats receipt prints as it scrolls in */
      if(receipt&&rwrapTop<1e9){
        sPP=reduced?1:lp(sPP,clamp((vh*0.92-rwrapTop)/(vh*0.62),0,1),f2);
        stv(receipt,'--pp',sPP.toFixed(3));
      }
      /* footer word fill */
      if(fword)stv(fword,'--fill',(clamp((y+vh-(docH-460))/430,0,1)*100).toFixed(1)+'%');
      /* QR morph state: which chapter is on screen */
      if(desk&&morphEl&&morphCells.length&&chapTops){
        var st2=0;
        for(var ci=0;ci<chapTops.length;ci++){if(chapTops[ci]<vh*0.6)st2=chapNums[ci].n;}
        if(ctaTop<vh*0.6)st2=0;
        if(st2!==morphSt){morphSt=st2;morphTo(st2);}
      }
      /* marquee */
      if(mqTrack&&!reduced){
        if(mqTrack.style.animation!=='none')mqTrack.style.animation='none';
        if(mqHalf<=0)mqMeasure();
        mqVel*=0.94;
        mqX-=(0.85+mqVel)*mqDir*dt;
        if(mqHalf>0){
          if(mqX<=-mqHalf)mqX+=mqHalf;
          if(mqX>0)mqX-=mqHalf;
        }
        mqTrack.style.transform='translate3d('+mqX.toFixed(1)+'px,0,0)';
      }
      /* scanner cursor ring + lamp trail */
      if(fine&&ring&&!reduced){
        crx=lp(crx,pmx,0.18);cry=lp(cry,pmy,0.18);
        stl(ring,'left',crx.toFixed(1)+'px');stl(ring,'top',cry.toFixed(1)+'px');
        if(lamp){
          clx=lp(clx,pmx,0.07);cly=lp(cly,pmy,0.07);
          stl(lamp,'transform','translate3d('+clx.toFixed(1)+'px,'+cly.toFixed(1)+'px,0)');
        }
      }
      /* pointer-driven touches, once per frame */
      if(doPtr){
        if(spotRs){
          spots.forEach(function(c,i){
            var r=spotRs[i];
            if(pmx<r.left-80||pmx>r.right+80||pmy<r.top-80||pmy>r.bottom+80)return;
            c.style.setProperty('--mx',(pmx-r.left)+'px');
            c.style.setProperty('--my',(pmy-r.top)+'px');
          });
        }
        if(heroR&&heroR.bottom>0&&phoneW){
          var nx=(pmx-heroR.left)/heroR.width-0.5;
          var ny=(pmy-heroR.top)/heroR.height-0.5;
          phoneW.style.setProperty('--ry',(-11+nx*10).toFixed(2)+'deg');
          phoneW.style.setProperty('--rx',(4-ny*8).toFixed(2)+'deg');
        }
        if(glowRs){
          glowCells.forEach(function(c,i){
            var r=glowRs[i];
            var d=Math.hypot(pmx-(r.left+r.width/2),pmy-(r.top+r.height/2));
            if(d<110){
              var k=1-d/110;
              c.style.transform='scale('+(1+k*0.6).toFixed(2)+')';
              c.style.boxShadow='0 0 '+(k*11).toFixed(1)+'px rgba(52,211,153,'+(k*0.9).toFixed(2)+')';
              c.style.backgroundColor=k>0.3?'#34D399':'';
              c._qg=true;
            }else if(c._qg){
              c._qg=false;
              c.style.transform='';c.style.boxShadow='';c.style.backgroundColor='';
            }
          });
        }
        pmoved=false;
      }
      /* interactive QR: cells repel the cursor and settle back when it leaves */
      if(repelRs){
        bigCells.forEach(function(c,i){
          var r=repelRs[i];
          var ox=c._qx||0,oy=c._qy||0;
          var cx=r.left+r.width/2-ox,cy=r.top+r.height/2-oy;
          var dx=cx-pmx,dy=cy-pmy;
          var d=Math.hypot(dx,dy);
          if(repelNear&&d<150){
            var k=1-d/150;
            var fo=k*44;
            c._qx=dx/(d||1)*fo;c._qy=dy/(d||1)*fo;
            c.style.transform='translate('+c._qx.toFixed(1)+'px,'+c._qy.toFixed(1)+'px) scale('+(1+k*0.5).toFixed(2)+')';
            c.style.opacity='1';
          }else if(ox||oy){
            c._qx=0;c._qy=0;
            c.style.transform='';c.style.opacity='';
          }
        });
        repelWas=repelNear;
      }
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    /* ---------- "+1 scan" pips floating off the hero phone ---------- */
    if(!reduced){
      setInterval(function(){
        var wrap=doc.getElementById('ql-phone-wrap');
        if(!wrap)return;
        var wr=wrap.getBoundingClientRect();
        if(wr.bottom<100||wr.top>window.innerHeight)return;
        var pip=doc.createElement('span');
        pip.className='qx-scanpip';
        pip.textContent='+1';
        pip.style.left=(12+Math.random()*56).toFixed(0)+'%';
        wrap.appendChild(pip);
        setTimeout(function(){if(pip.parentNode)pip.parentNode.removeChild(pip);},2300);
      },4800);
    }
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',boot);
  else setTimeout(boot,0);
})();
"""
